import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import cloneDeep from 'lodash/cloneDeep'
import isEmpty from 'lodash/isEmpty'
import { IModifiedPlan } from 'src/types/modifiedPlan.interface'
import { IMultiFamilyGridPlan } from 'src/types/multiFamily.grid.interface'
import {
  IShellPlan,
  ShellArea,
  ShellGarage,
  ShellOverview,
  ShellStructure,
  ShellStructureSpecifications,
} from 'src/types/shell.interface'
import {
  IUnitPlan,
  UnitAdditionalFacilities,
  UnitAdditionalRooms,
  UnitArea,
  UnitGarage,
  UnitOverview,
  UnitPrice,
  UnitStructure,
  UnitStructureSpecifications,
} from 'src/types/unit.interface'

import {
  defaultCommunityNameLabel,
  defaultCommunityNumberLabel,
  defaultPlanElevationLabel,
} from '../../constants/modal.constants'
import {
  COMMUNITY,
  GET_MODSY_SPECS,
  LOAD_PDF_UPLOAD_FOR_AUTO_EXTRACT,
  PLAN,
} from '../../lib/api-routes'
import {
  CancelTokenSource,
  HttpRequestConfig,
  instance,
} from '../../lib/axiosInstance'
import {
  buildingTypes,
  familyTypes,
  planCategories,
  structureTypes,
  TABS,
} from '../../lib/constants'
import { IJdePriceStatusOverlay } from '../../types/jdePriceStatus.interface'
import {
  AdditionalOptions,
  AdditionalPlanAttributes,
  DocumentLink,
  GetPlanParams,
  ICommunityName,
  ICommunityNumber,
  IDraftPlan,
  IExistingPlan,
  IPlan,
  IPlanElevation,
  IPlanError,
  IPlanModalInfo,
  ParentInfo,
  PlanMasterData,
  PlanOverview,
  PlanType,
  ProcessedValidationErrorItem,
  SourceRegionalPlanDocument,
  Specifications,
  SupplementalDocuments,
  SupportingDocuments,
} from '../../types/plan.interface'
import { isLennarComUrl } from '../../utils/lennarUrl.util'
import { isUnsafeObjectKey } from '../../utils/objectKey.util'
import { getCommunityData, toPlanToView } from '../../utils/planFormMapper.util'
import { getJdePriceStatus as requestJdePriceStatus } from '../actions/get-jde-price-status'
import { getPlan } from '../actions/get-plan'
import { getShell } from '../actions/get-shell'
import { getUnit } from '../actions/get-unit'
import {
  initialDraftPlan,
  initialSpecifications,
} from './createPlanIntialValues'

/**
 * Helper function to check if two documents are the same.
 * Compares by fileId first (for saved documents), then falls back to fileName + fileTab for new uploads.
 */
const isSameDocument = (
  doc1: SupportingDocuments,
  doc2: SupportingDocuments
): boolean => {
  return (
    doc1.fileId === doc2.fileId ||
    (doc1.fileName === doc2.fileName && doc1.fileTab === doc2.fileTab)
  )
}

export interface IPlanModalValidation {
  activeCommunityIndex: number
  communitiesExistError: boolean
  divisionValidationError: boolean
  existingPlans: IExistingPlan[]
  isLoading: boolean
  planDivisionRequired: boolean
  planElevationCharLengthError: boolean
  planElevationExistError: boolean
  planRegionRequired: boolean
  planRequiredField: boolean
  showCorePlanExistError: boolean
  showCorePlanExistErrorMessage: string
  showPlanError: boolean
  showPlanExistError: boolean
  showPlanExistErrorMessage: string
  showPlanLengthErrorForShellPlans: boolean
  showPlanLengthErrorForUnitPlans: boolean
}

export interface ExtractedPlanInfo {
  communityId?: string
  divisionName?: string
  plan?: string
  planCategory?: string
  planElevation?: string
  regionName?: string
}

export interface IAutoExtractedData {
  error?: string | null
  failedPlanIds?: string[]
  fileUploadedBy?: string
  planIds?: string[]
  successfullyUpdatedDraftPlan: boolean
  successfulPlans?: ExtractedPlanInfo[]
}

interface ModsySpecs {
  error: boolean
  isLoading: boolean
  specs: Record<string, DocumentLink>
}

export interface ICreatePlan {
  autoExtractedData: IAutoExtractedData
  draftPlan: IDraftPlan
  draftPlanModalInfo: IPlanModalInfo
  draftPlanModalValidation: IPlanModalValidation
  jdePriceStatus?: IJdePriceStatusOverlay | null
  legacyPlan: IDraftPlan
  legacyShell: IDraftPlan
  legacyUnit: IDraftPlan
  modsySpecs?: ModsySpecs
}

/**
 * Helper function to propagate newly added documents from the primary plan to other plans in bulk edit mode.
 * Handles document deduplication and tracks initial documents for each plan.
 *
 * @param state - The Redux state
 * @param id - The current plan ID being edited
 * @param supportingDocuments - The updated supporting documents array
 * @param multiElevationPlans - The object containing all elevation plans (multiElevationShellPlans, multiElevationUnitPlans, or multiElevationPlans)
 */
const propagateDocumentsInBulkEdit = <
  T extends {
    supplementalDocuments?: SupportingDocuments[]
  },
>(
  state: ICreatePlan,
  id: string,
  supportingDocuments: SupportingDocuments[],
  multiElevationPlans: Record<string, T>
): void => {
  if (!state.draftPlan.isBulkEdit || !state.draftPlan.planInfoData?.planId) {
    return
  }

  const primaryPlanId = state.draftPlan.planInfoData.planId[0]
  if (id !== primaryPlanId) {
    return
  }

  const basePlanInitialDocs =
    state.draftPlan.initialBulkEditDocuments[primaryPlanId] || []

  const newlyAddedDocs = supportingDocuments.filter(
    (doc: SupportingDocuments) => {
      return !basePlanInitialDocs.some((initialDoc) =>
        isSameDocument(initialDoc, doc)
      )
    }
  )

  if (newlyAddedDocs.length === 0) {
    return
  }

  state.draftPlan.planInfoData.planId.forEach((otherPlanId, index) => {
    if (index === 0 || otherPlanId === primaryPlanId) {
      return
    }

    const otherPlan = multiElevationPlans[otherPlanId]
    if (otherPlan) {
      const otherPlanInitialDocs =
        state.draftPlan.initialBulkEditDocuments[otherPlanId] || []
      const currentOtherPlanDocs = otherPlan.supplementalDocuments || []

      const docsAddedToThisPlan = currentOtherPlanDocs.filter(
        (doc: SupportingDocuments) => {
          const wasInitial = otherPlanInitialDocs.some(
            (initialDoc) => initialDoc.fileId === doc.fileId
          )
          const isFromBasePlan = basePlanInitialDocs.some(
            (baseDoc) => baseDoc.fileId === doc.fileId
          )
          return !wasInitial && !isFromBasePlan
        }
      )

      const mergedDocs = [
        ...otherPlanInitialDocs,
        ...docsAddedToThisPlan,
        ...newlyAddedDocs,
      ]

      const uniqueDocs = mergedDocs.filter(
        (doc, index, self) =>
          index === self.findIndex((d) => isSameDocument(d, doc))
      )

      otherPlan.supplementalDocuments = uniqueDocs
      state.draftPlan.initialBulkEditDocuments[otherPlanId] = [
        ...otherPlanInitialDocs,
        ...newlyAddedDocs,
      ]
    }
  })

  // Update base plan's initial documents
  state.draftPlan.initialBulkEditDocuments[primaryPlanId] = supportingDocuments
}

/**
 * Helper function to propagate attribute changes from the primary plan to other plans in bulk edit mode.
 * Used for patching plan data sections across all elevation plans.
 *
 * @param state - The Redux state
 * @param multiElevationPlans - The object containing all elevation plans
 * @param section - The section name to update
 * @param value - The new value to merge into the section
 */
const propagateAttributesInBulkEdit = <T, K extends keyof T>(
  state: { draftPlan: IDraftPlan },
  multiElevationPlans: Record<string, T>,
  section: K,
  value: Partial<T[K]>
): void => {
  if (!state.draftPlan.isBulkEdit || !state.draftPlan.planInfoData?.planId) {
    return
  }

  const isBasePlan = state.draftPlan.planEntryFormElevationIndex.index === 0

  if (!isBasePlan) {
    return
  }

  Object.values(multiElevationPlans).forEach((plan, planIndex) => {
    if (planIndex > 0 && plan && section) {
      const planSection = plan[section]
      if (planSection && typeof planSection === 'object') {
        const merged = { ...planSection, ...value }
        Object.assign(plan, { [section]: merged })
      }
    }
  })
}

/**
 * Helper function to propagate document changes from the primary elevation to secondary elevations
 * in multi-elevation create mode. This ensures that documents added/deleted on the primary elevation
 * are reflected on secondary elevations while preserving elevation-specific documents.
 *
 * Note: This function handles DATA SYNCHRONIZATION. The isDocumentPropagationEnabled flag
 * controls UI DISPLAY behavior (via selectors) and is intentionally not checked here.
 *
 * @param state - The Redux state
 * @param oldPrimaryDocs - The documents from primary BEFORE the change (used to detect deletions)
 * @param newPrimaryDocs - The documents from primary AFTER the change
 * @param multiElevationPlans - The object containing all elevation plans
 */
const propagateDocumentChangesInMultiElevation = <
  T extends {
    supplementalDocuments?: SupportingDocuments[]
  },
>(
  state: ICreatePlan,
  oldPrimaryDocs: SupportingDocuments[],
  newPrimaryDocs: SupportingDocuments[],
  multiElevationPlans: Record<string, T>
): void => {
  if (
    state.draftPlan.isBulkEdit ||
    !state.draftPlan.planInfoData?.planId ||
    state.draftPlan.planInfoData.planId.length <= 1
  ) {
    return
  }

  const primaryPlanId = state.draftPlan.planInfoData.planId[0]
  const normalizedOldPrimaryDocs = oldPrimaryDocs || []
  const normalizedNewPrimaryDocs = newPrimaryDocs || []

  state.draftPlan.planInfoData.planId.forEach((otherPlanId, index) => {
    if (index === 0 || otherPlanId === primaryPlanId) {
      return
    }

    const otherPlan = multiElevationPlans[otherPlanId]
    if (otherPlan) {
      const currentOtherPlanDocs = otherPlan.supplementalDocuments || []

      // A document is elevation-specific if it was NEVER in primary docs.
      // This means it was added directly to this elevation.
      // Docs that were in old primary but not in new primary are DELETED and should be removed.
      const elevationSpecificDocs = currentOtherPlanDocs.filter(
        (doc: SupportingDocuments) => {
          const wasInOldPrimary = normalizedOldPrimaryDocs.some((oldDoc) =>
            isSameDocument(oldDoc, doc)
          )
          const isInNewPrimary = normalizedNewPrimaryDocs.some((newDoc) =>
            isSameDocument(newDoc, doc)
          )
          // Keep the doc only if it was NEVER in primary (elevation-specific)
          // If it was in old primary but not new primary, it was deleted -> don't keep
          return !wasInOldPrimary && !isInNewPrimary
        }
      )

      const mergedDocs = [...normalizedNewPrimaryDocs, ...elevationSpecificDocs]

      const uniqueDocs = mergedDocs.filter(
        (doc, idx, self) =>
          idx === self.findIndex((d) => isSameDocument(d, doc))
      )

      otherPlan.supplementalDocuments = uniqueDocs
    }
  })
}

/**
 * Helper function to propagate document changes (additions and deletions) from the primary plan to other plans in bulk edit mode.
 * Unlike propagateDocumentsInBulkEdit, this handles both additions AND deletions.
 *
 * @param state - The Redux state
 * @param planId - The current plan ID being edited
 * @param newDocuments - The new supporting documents array
 * @param multiElevationPlans - The object containing all elevation plans
 */
const propagateDocumentChangesInBulkEdit = <
  T extends {
    supplementalDocuments?: SupportingDocuments[]
  },
>(
  state: ICreatePlan,
  planId: string,
  newDocuments: SupportingDocuments[],
  multiElevationPlans: Record<string, T>
): void => {
  if (!state.draftPlan.isBulkEdit || !state.draftPlan.planInfoData?.planId) {
    return
  }

  const primaryPlanId = state.draftPlan.planInfoData.planId[0]
  if (planId !== primaryPlanId) {
    return
  }

  const basePlanInitialDocs =
    state.draftPlan.initialBulkEditDocuments[primaryPlanId] || []

  const newlyAddedDocs = newDocuments.filter(
    (doc: SupportingDocuments) =>
      !basePlanInitialDocs.some((initialDoc) => isSameDocument(initialDoc, doc))
  )

  const deletedDocs = basePlanInitialDocs.filter(
    (initialDoc: SupportingDocuments) =>
      !newDocuments.some((doc) => isSameDocument(initialDoc, doc))
  )

  if (newlyAddedDocs.length === 0 && deletedDocs.length === 0) {
    return
  }

  state.draftPlan.planInfoData.planId.forEach((otherPlanId, index) => {
    if (index === 0 || otherPlanId === primaryPlanId) {
      return
    }

    const otherPlan = multiElevationPlans[otherPlanId]
    if (otherPlan) {
      const otherPlanInitialDocs =
        state.draftPlan.initialBulkEditDocuments[otherPlanId] || []
      const currentOtherPlanDocs = otherPlan.supplementalDocuments || []

      const docsAddedToThisPlan = currentOtherPlanDocs.filter(
        (doc: SupportingDocuments) => {
          const wasInitial = otherPlanInitialDocs.some((initialDoc) =>
            isSameDocument(initialDoc, doc)
          )
          const isFromBasePlan = basePlanInitialDocs.some((baseDoc) =>
            isSameDocument(baseDoc, doc)
          )
          return !wasInitial && !isFromBasePlan
        }
      )

      let updatedDocs = [...otherPlanInitialDocs, ...docsAddedToThisPlan]

      if (deletedDocs.length > 0) {
        updatedDocs = updatedDocs.filter(
          (doc) =>
            !deletedDocs.some((deletedDoc) => isSameDocument(deletedDoc, doc))
        )
      }

      updatedDocs = [...updatedDocs, ...newlyAddedDocs]

      const uniqueDocs = updatedDocs.filter(
        (doc, index, self) =>
          index === self.findIndex((d) => isSameDocument(d, doc))
      )

      otherPlan.supplementalDocuments = uniqueDocs

      let newInitialDocs = otherPlanInitialDocs.filter(
        (doc) =>
          !deletedDocs.some((deletedDoc) => isSameDocument(deletedDoc, doc))
      )
      newInitialDocs = [...newInitialDocs, ...newlyAddedDocs]
      state.draftPlan.initialBulkEditDocuments[otherPlanId] = newInitialDocs
    }
  })

  state.draftPlan.initialBulkEditDocuments[primaryPlanId] = newDocuments
}

const initialCommunityObj: ICommunityName = {
  communityCode: '',
  communityName: '',
  divisionCode: '',
}
const communityNumberObj: ICommunityNumber = {
  cNameShowErrorMsg: false,
  cNameVal: initialCommunityObj,
  cNumberIsEmptyError: false,
  cNumberVal: '',
  communityName: defaultCommunityNameLabel,
  communityNumberLabel: defaultCommunityNumberLabel,
  divisionValidationError: false,
  error: false,
  loading: false,
  submitError: false,
  submitSuccess: false,
}

export const planElevationObj: IPlanElevation = {
  error: false,
  label: defaultPlanElevationLabel,
  success: false,
  value: '',
}

const planModalInitialState: IPlanModalInfo = {
  autofill: '',
  buildingType: buildingTypes.SHELL,
  bypassCorePlanValidation: false,
  communityName: '',
  communityNumber: [communityNumberObj],
  division: '',
  divisionCode: '',
  familyType: familyTypes.SINGLE,
  isCreatePlan: true,
  plan: '',
  planCategory: planCategories.COMMUNITY,
  planElevation: [planElevationObj],
  region: '',
  structureType: structureTypes.TOWN_HOME,
  submitted: false,
}

const initialPlanModalValidation: IPlanModalValidation = {
  activeCommunityIndex: 0,
  communitiesExistError: false,
  divisionValidationError: false,
  existingPlans: [],
  isLoading: false,
  planDivisionRequired: false,
  planElevationCharLengthError: false,
  planElevationExistError: false,
  planRegionRequired: false,
  planRequiredField: false,
  showCorePlanExistError: false,
  showCorePlanExistErrorMessage: '',
  showPlanError: false,
  showPlanExistError: false,
  showPlanExistErrorMessage: '',
  showPlanLengthErrorForShellPlans: false,
  showPlanLengthErrorForUnitPlans: false,
}

export interface ISpecifications {
  label: string
  link: string
}

export const initialElevationPlanData: IPlan = {
  additionalOptionsData: {} as AdditionalOptions,
  additionalPlanAttributes: {} as AdditionalPlanAttributes,
  isCeilingHeightsRequired: false,
  planMasterData: {} as PlanMasterData,
  specifications: initialSpecifications,
  supplementalDocuments: [] as SupportingDocuments[],
}

export const initialElevationShellPlanData: IShellPlan = {
  _etag: '',
  communityPlanId: '',
  createdByCorporate: false,
  garages: {} as ShellGarage,
  parentInfo: {} as ParentInfo,
  planCreatedBy: '',
  planCreatedDateTime: '',
  shellArea: {} as ShellArea,
  shellOverview: {} as ShellOverview,
  shellStructure: {} as ShellStructure,
  structureSpecifications: {} as ShellStructureSpecifications,
  submitted: false,
  supplementalDocuments: [] as SupportingDocuments[],
  versionId: 0,
}

export const initialElevationUnitPlanData: IUnitPlan = {
  _etag: '',
  communityPlanId: '',
  createdByCorporate: false,
  garages: {} as UnitGarage,
  parentInfo: {} as ParentInfo,
  planCreatedBy: '',
  planCreatedDateTime: '',
  structureSpecifications: {} as UnitStructureSpecifications,
  submitted: false,
  supplementalDocuments: [] as SupportingDocuments[],
  unitAdditionalFacilities: {} as UnitAdditionalFacilities,
  unitAdditionalRooms: {} as UnitAdditionalRooms,
  unitArea: {} as UnitArea,
  unitOverview: {} as UnitOverview,
  unitPrice: {} as UnitPrice,
  unitStructure: {} as UnitStructure,
  versionId: 0,
}

const initialState: ICreatePlan = {
  autoExtractedData: {} as IAutoExtractedData,
  draftPlan: initialDraftPlan,
  draftPlanModalInfo: {} as IPlanModalInfo,
  draftPlanModalValidation: {} as IPlanModalValidation,
  jdePriceStatus: null,
  legacyPlan: initialDraftPlan,
  legacyShell: initialDraftPlan,
  legacyUnit: initialDraftPlan,
  modsySpecs: {} as ModsySpecs,
}

interface State {
  draftPlan: IDraftPlan
}
interface PatchPlanDataAction<T extends keyof IPlan> {
  payload: {
    section: T
    value: Partial<IPlan[T]>
  }
}

interface PatchShellPlanDataAction<T extends keyof IShellPlan> {
  payload: {
    section: T
    value: Partial<IShellPlan[T]>
  }
}

interface PatchUnitPlanDataAction<T extends keyof IUnitPlan> {
  payload: {
    section: T
    value: Partial<IUnitPlan[T]>
  }
}

interface SetPlanDataAction<T extends keyof IPlan> {
  payload: {
    section: T
    key: string
    value: string | number | boolean | object | string[] | undefined
  }
}

interface RemovePlanKeyAction<T extends keyof IPlan> {
  payload: {
    section: T
    key: string
  }
}

interface SetShellPlanDataAction<T extends keyof IShellPlan> {
  payload: {
    section: T
    key: string
    value: string | number | boolean | object | string[] | undefined
  }
}

interface SetUnitPlanDataAction<T extends keyof IUnitPlan> {
  payload: {
    section: T
    key: string
    value: string | number | boolean | object | string[] | undefined
  }
}

interface SetDraftPlanAction<K extends keyof IDraftPlan> {
  payload: {
    key: K
    value: IDraftPlan[K]
  }
}

interface SetDraftPlanModalValueAction<K extends keyof IPlanModalInfo> {
  payload: {
    id?: string | number
    key: K
    value: IPlanModalInfo[K]
  }
}

interface ResetDraftPlanModalPayload {
  familyType?: string
  isAddElevation?: boolean
  isCopyPlan?: boolean
  isForceReset?: boolean
  isPrePopulatePlan?: boolean
  selectedPlan?: IModifiedPlan | IMultiFamilyGridPlan
}

export interface CommunityDataResponse {
  communityInfo: ICommunityName
}

interface ModsySpecsResponse {
  communityId?: string
  name?: string
  url?: string
}

export interface PlanDataByIdResponse {
  legacyPlan?: PlanType
  plan?: { submitted?: boolean }
  planId?: string
}

export interface ShellPlanResponse extends IShellPlan {
  legacyShell?: ShellPlanResponse
  plan: string
  planName: string
  supportingDocuments: SupportingDocuments[]
}

export interface UnitPlanResponse extends IUnitPlan {
  isLegacyPlan?: boolean
  legacyUnit?: UnitPlanResponse
  plan: string
  planName: string
  supportingDocuments: SupportingDocuments[]
}

export interface BulkEditPlanResponse {
  _etag?: string
  communityPlanId: string
  division?: string
  garages: ShellGarage & UnitGarage
  hidden?: boolean
  parentInfo: ParentInfo
  plan: string
  planCreatedBy: string
  planCreatedDateTime: string
  planId: string
  planOverview: PlanOverview & { planElevation: string }
  planStructure?: { foundationType?: string }
  shellArea: ShellArea
  shellOverview: ShellOverview
  shellStructure: ShellStructure
  specifications?: Specifications
  structureSpecifications: ShellStructureSpecifications &
    UnitStructureSpecifications
  submitted: boolean
  supplementalDocuments?: SupplementalDocuments
  supportingDocuments: SupportingDocuments[]
  unitAdditionalFacilities: UnitAdditionalFacilities
  unitAdditionalRooms: UnitAdditionalRooms
  unitArea: UnitArea
  unitOverview: UnitOverview
  unitPrice: UnitPrice
  unitStructure: UnitStructure
  versionId: number
}

const fetchCommunityDataById = createAsyncThunk(
  'communityName/fetchCommunityDataById',
  async (data: { communityPhaseNumber: string; index: number }) => {
    try {
      const response = await instance.get<CommunityDataResponse>(COMMUNITY, {
        params: {
          communityPhaseNumber: data.communityPhaseNumber,
        },
      })
      return response.data
    } catch (error) {
      throw Error('Failed to Fetch the Community Name')
    }
  }
)

const getPlanDataByPlanId = createAsyncThunk(
  'getPlanDataByPlanId',
  async (data: { planId: string; submitted: boolean }) => {
    try {
      const response = await instance.get<PlanDataByIdResponse>(PLAN, {
        params: data,
      })
      return response.data
    } catch (error) {
      throw Error('Failed to Fetch the PlanData By PlanId')
    }
  }
)

/**
 * LTGC-5711: fetches the source Approved Regional Plan of a derived draft into
 * draftPlan.sourceRegionalPlan without touching the form state — the diff
 * engine compares the form against it to drive the Submit button.
 */
export const fetchSourceRegionalPlan = createAsyncThunk(
  'fetchSourceRegionalPlan',
  async (sourcePlanId: string) => {
    const response = await instance.get<SourceRegionalPlanDocument>(PLAN, {
      params: { planId: sourcePlanId, submitted: true },
    })
    return response.data
  }
)

export const getPlanSubset = createAsyncThunk(
  PLAN,
  async (params: GetPlanParams) => {
    return (await getPlan(params))?.data as PlanType
  }
)

export const getShellPlan = createAsyncThunk(
  '/shell',
  async (communityPlanId: string) => {
    return (await getShell(communityPlanId))?.data as ShellPlanResponse
  }
)

export const getUnitPlan = createAsyncThunk(
  '/unit',
  async (communityPlanId: string) => {
    return (await getUnit(communityPlanId))?.data as UnitPlanResponse
  }
)

export const getShellPlanSupplementDocuments = createAsyncThunk(
  'shellPlanSupplementalDocs',
  async (communityPlanId: string) => {
    return (await getShell(communityPlanId))?.data as ShellPlanResponse
  }
)

export const getUnitPlanSupplementDocuments = createAsyncThunk(
  'unitPlanSupplementalDocs',
  async (communityPlanId: string) => {
    return (await getUnit(communityPlanId))?.data as UnitPlanResponse
  }
)

export const getShellPlanCopy = createAsyncThunk(
  'getShellPlanCopy',
  async (communityPlanId: string) => {
    return (await getShell(communityPlanId))?.data as ShellPlanResponse
  }
)

export const getUnitPlanCopy = createAsyncThunk(
  'getUnitPlanCopy',
  async (communityPlanId: string) => {
    return (await getUnit(communityPlanId))?.data as UnitPlanResponse
  }
)

export const getModsySpecs = createAsyncThunk(
  'getModsySpecs',
  async (communityId: string) => {
    const response = await instance.get<ModsySpecsResponse>(
      GET_MODSY_SPECS(communityId)
    )
    return response?.data
  }
)

export const getJdePriceStatus = createAsyncThunk(
  'getJdePriceStatus',
  async (params: {
    communityId: string
    communityPlanId: string
    plan: string
    planElevation?: string
  }) => {
    const response = await requestJdePriceStatus({
      communityId: params.communityId,
      plan: params.plan,
      planElevation: params.planElevation,
    })
    return response?.data
  }
)

/**
 * Applies the stored JDE price/status overlay to the currently mapped plan or
 * unit document. JDE values always win over stored document values, but only
 * for the document whose id matches the overlay's communityPlanId — a stale
 * overlay from a previously viewed plan never applies to a different plan.
 */
/**
 * Keeps the lennar.com page of a plan opened for editing so its title can link
 * to it (LTGC-5759). A url that is not on lennar.com is dropped, not linked.
 */
const recordLennarUrl = (
  state: ICreatePlan,
  planId: string | undefined,
  lennarUrl: unknown
): void => {
  const planKey = planId?.toLowerCase()
  if (!planKey || isUnsafeObjectKey(planKey)) {
    return
  }
  const lennarUrls = state.draftPlan.lennarUrls ?? {}
  if (isLennarComUrl(lennarUrl)) {
    lennarUrls[planKey] = lennarUrl
  } else {
    delete lennarUrls[planKey]
  }
  state.draftPlan.lennarUrls = lennarUrls
}

const applyJdePriceStatusOverlay = (state: ICreatePlan): void => {
  const overlay = state.jdePriceStatus
  if (!overlay) {
    return
  }
  const { communityPlanId, planBasePrice, planStatus } = overlay
  const basePriceText = planBasePrice.toString()

  if (state.draftPlan.planInfoData?.planId?.[0] === communityPlanId) {
    state.draftPlan.primaryElevationPlan.planMasterData = {
      ...state.draftPlan.primaryElevationPlan.planMasterData,
      baseHousePrice: basePriceText,
      planStatus,
    }
  }

  const elevationPlan = state.draftPlan.multiElevationPlans[communityPlanId]
  if (elevationPlan) {
    state.draftPlan.multiElevationPlans[communityPlanId] = {
      ...elevationPlan,
      planMasterData: {
        ...elevationPlan.planMasterData,
        baseHousePrice: basePriceText,
        planStatus,
      },
    }
  }

  if (
    state.draftPlan.primaryElevationUnitPlan?.communityPlanId ===
    communityPlanId
  ) {
    state.draftPlan.primaryElevationUnitPlan.unitOverview = {
      ...state.draftPlan.primaryElevationUnitPlan.unitOverview,
      unitStatus: planStatus,
    }
    state.draftPlan.primaryElevationUnitPlan.unitPrice = {
      ...state.draftPlan.primaryElevationUnitPlan.unitPrice,
      baseUnitPrice: planBasePrice,
    }
  }

  const elevationUnitPlan =
    state.draftPlan.multiElevationUnitPlans[communityPlanId]
  if (elevationUnitPlan) {
    state.draftPlan.multiElevationUnitPlans[communityPlanId] = {
      ...elevationUnitPlan,
      unitPrice: {
        ...elevationUnitPlan.unitPrice,
        baseUnitPrice: planBasePrice,
      },
    }
  }
}

export const getBulkEditPlans = createAsyncThunk(
  'getBulkEditPlans',
  async (params: {
    planIds: string[]
    familyType: string
    buildingType?: string
    submitted: boolean
  }) => {
    const { buildingType, familyType, planIds, submitted } = params

    if (familyType === familyTypes.MULTI) {
      if (buildingType === buildingTypes.SHELL) {
        const shellPromises = planIds.map((planId) => getShell(planId))
        const results = await Promise.all(shellPromises)
        return results.map((res) => res?.data as BulkEditPlanResponse)
      } else if (buildingType === buildingTypes.UNIT) {
        const unitPromises = planIds.map((planId) => getUnit(planId))
        const results = await Promise.all(unitPromises)
        return results.map((res) => res?.data as BulkEditPlanResponse)
      }
    } else {
      const planPromises = planIds.map((planId) =>
        getPlan({ planId, submitted })
      )
      const results = await Promise.all(planPromises)
      return results.map((res) => res?.data as BulkEditPlanResponse)
    }
    return [] as BulkEditPlanResponse[]
  }
)

const createParentInfo = (
  pathname: string,
  elevationType: 'shell' | 'unit',
  parentInfo: ParentInfo | undefined,
  communityPlanId: string,
  versionId: number,
  parentPlanName: string
): ParentInfo => {
  const isAddElevation = pathname.includes(`add-${elevationType}-elevation`)
  const isPrePopulate = pathname.includes(`pre-populate-${elevationType}`)

  if (isAddElevation) {
    return parentInfo?.parentOrChildParentId
      ? {
          parentOrChildParentId: parentInfo.parentOrChildParentId,
          parentPlanName: parentInfo.parentPlanName,
          typeOfParent: parentInfo.typeOfParent,
          versionId: parentInfo.versionId,
        }
      : ({} as ParentInfo)
  }

  if (isPrePopulate) {
    return {} as ParentInfo
  }

  return {
    parentOrChildParentId: communityPlanId,
    parentPlanName,
    typeOfParent: parentInfo?.typeOfParent === 'parent' ? 'child' : 'parent',
    versionId,
  }
}

const uploadFileForAutoExtract = createAsyncThunk(
  'uploadFileForAutoExtract',
  async (params: {
    uploadParams: FormData
    config: HttpRequestConfig
    cancelToken: CancelTokenSource
  }) => {
    try {
      const response = await instance.post(
        LOAD_PDF_UPLOAD_FOR_AUTO_EXTRACT,
        params.uploadParams,
        {
          ...params.config,
          cancelToken: params.cancelToken.token,
        }
      )
      return response.data
    } catch (error) {
      throw Error('Failed to Upload File for Auto Extract')
    }
  }
)

const createPlanSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommunityDataById.pending, (state) => {
        if (state.draftPlanModalInfo.communityNumber) {
          const activeCommunityIndex =
            state.draftPlanModalValidation.activeCommunityIndex
          state.draftPlanModalInfo.communityNumber[activeCommunityIndex].error =
            false
          state.draftPlanModalInfo.communityNumber[
            activeCommunityIndex
          ].divisionValidationError = false
          state.draftPlanModalInfo.divisionCode = ''
          state.draftPlanModalInfo.communityNumber[
            activeCommunityIndex
          ].communityName = ''
          state.draftPlanModalInfo.communityNumber[
            activeCommunityIndex
          ].cNameVal = {}
          state.draftPlanModalInfo.communityNumber[
            activeCommunityIndex
          ].loading = true
        }
      })
      .addCase(fetchCommunityDataById.fulfilled, (state, action) => {
        if (state.draftPlanModalInfo.communityNumber) {
          const activeCommunityIndex =
            state.draftPlanModalValidation.activeCommunityIndex
          state.draftPlanModalInfo.communityNumber[
            activeCommunityIndex
          ].loading = false
          state.draftPlanModalInfo.communityNumber[
            activeCommunityIndex
          ].cNameShowErrorMsg = false
          state.draftPlanModalInfo.communityNumber[
            activeCommunityIndex
          ].cNameVal = action.payload?.communityInfo
          state.draftPlanModalInfo.communityNumber[
            activeCommunityIndex
          ].communityName = action.payload?.communityInfo?.communityName
          state.draftPlanModalInfo.divisionCode =
            action.payload?.communityInfo?.divisionCode
          state.draftPlanModalInfo.communityNumber[
            activeCommunityIndex
          ].divisionValidationError = false
        }
      })
      .addCase(fetchCommunityDataById.rejected, (state) => {
        if (state.draftPlanModalInfo.communityNumber) {
          state.draftPlanModalValidation.isLoading = false
          const activeCommunityIndex =
            state.draftPlanModalValidation.activeCommunityIndex
          state.draftPlanModalInfo.communityNumber[
            activeCommunityIndex
          ].cNameShowErrorMsg = true
          state.draftPlanModalInfo.communityNumber[
            activeCommunityIndex
          ].loading = false
        }
      })
      .addCase(getModsySpecs.pending, (state) => {
        state.modsySpecs = {
          error: false,
          isLoading: true,
          specs: { ...state.modsySpecs?.specs },
        }
      })
      .addCase(getModsySpecs.fulfilled, (state, action) => {
        const communityNumber = state.draftPlan.planInfoData?.communityNumber
        const spec = action.payload
        const index = state.draftPlan.planEntryFormElevationIndex?.index
        const id =
          spec?.communityId ??
          communityNumber?.[index]?.cNumberVal ??
          action.meta.arg
        if (state.modsySpecs && state.modsySpecs?.specs && id) {
          state.modsySpecs.specs[id] = {
            ...state.modsySpecs.specs[id],
            label: spec?.name as string,
            link: spec?.url as string,
          }
        }
        if (state.modsySpecs) {
          state.modsySpecs.isLoading = false
          state.modsySpecs.error = false
        }
      })
      .addCase(getModsySpecs.rejected, (state) => {
        state.modsySpecs = {
          error: true,
          isLoading: false,
          specs: { ...state.modsySpecs?.specs },
        }
      })
      .addCase(getJdePriceStatus.fulfilled, (state, action) => {
        const jdeResponse = action.payload
        if (
          !jdeResponse?.found ||
          jdeResponse.planBasePrice === undefined ||
          jdeResponse.planStatus === undefined
        ) {
          return
        }
        state.jdePriceStatus = {
          communityPlanId: action.meta.arg.communityPlanId,
          planBasePrice: jdeResponse.planBasePrice,
          planStatus: jdeResponse.planStatus,
        }
        applyJdePriceStatusOverlay(state)
      })
      .addCase(getPlanDataByPlanId.pending, (state, action) => {
        state.draftPlan.isLoading = true
        if (
          state.jdePriceStatus &&
          state.jdePriceStatus.communityPlanId !== action.meta.arg.planId
        ) {
          state.jdePriceStatus = null
        }
      })
      // Tracked so submission can fail CLOSED while the answer is unknown: until
      // this settles we cannot tell an ordinary copy from an ARP-derived one,
      // and saving in that window writes a draft that is silently not derived.
      .addCase(fetchSourceRegionalPlan.pending, (state) => {
        state.draftPlan.isSourceRegionalPlanPending = true
      })
      .addCase(fetchSourceRegionalPlan.fulfilled, (state, action) => {
        state.draftPlan.isSourceRegionalPlanPending = false
        // Guarded so a broadened trigger can never install a non-ARP parent
        // (e.g. a core plan) as the diff source.
        const fetchedCategory =
          action.payload?.planOverview?.planCategory?.toLowerCase()
        if (fetchedCategory !== planCategories.APPROVED_REGIONAL_PLAN) {
          return
        }
        state.draftPlan.sourceRegionalPlan = action.payload
        // The draft IS derived from an Approved Regional Plan. Stamping here —
        // on the source fetch, not only on the copy-route load — survives the
        // races that skipped the mount-time fetch (userInfo arriving late) and
        // sessions restored from persisted local state.
        state.draftPlan.planInfoData.derivedFromApprovedRegionalPlan = true
      })
      .addCase(fetchSourceRegionalPlan.rejected, (state) => {
        state.draftPlan.isSourceRegionalPlanPending = false
        state.draftPlan.sourceRegionalPlan = null
      })
      .addCase(getPlanDataByPlanId.fulfilled, (state, action) => {
        state.draftPlan.isLoading = false
        // LTGC-5711: copying an ARP loads the ARP itself here — keep the raw
        // document so the diff engine can compare the form against it.
        const loadedPlanCategory = (
          action.payload as unknown as SourceRegionalPlanDocument
        )?.planOverview?.planCategory
        if (
          loadedPlanCategory?.toLowerCase() ===
          planCategories.APPROVED_REGIONAL_PLAN
        ) {
          state.draftPlan.sourceRegionalPlan =
            action.payload as unknown as SourceRegionalPlanDocument
          // The copy IS derived from an Approved Regional Plan, even though the
          // loaded source document carries no such flag itself. This is what
          // toPlanToSend reads to put the root flag on every save and submit —
          // without it a fresh copy submitted under the STANDARD community
          // validation (Roof Shape, Entry, ...) instead of the LTGC-5711
          // required-attribute set, and the server never stamped the draft.
          state.draftPlan.planInfoData.derivedFromApprovedRegionalPlan = true
        }
        const isLegacyDraftPlan =
          (action.payload?.legacyPlan?.isLegacyPlan &&
            !action.payload?.plan?.submitted) ||
          false
        if (action.payload?.legacyPlan?.planId) {
          const {
            additionalOptionsData,
            additionalPlanAttributes,
            parentInfo: newParentInfo,
            planInfoData,
            planMasterData,
            specifications,
            supplementalDocuments,
          } = toPlanToView(action.payload?.legacyPlan as PlanType, false)
          if (state.draftPlan.isEditPlan) {
            state.legacyPlan.planInfoData = {
              ...state.draftPlan.planInfoData,
              ...planInfoData,
            }
            state.legacyPlan.planEntryFormElevationIndex.id =
              planInfoData.planId[0]

            state.legacyPlan.isCeilingHeightsRequired =
              planInfoData?.isCeilingHeightsRequired ?? false

            state.legacyPlan.hidden = planInfoData?.hidden ?? false
          }
          if (
            state.legacyPlan.isEditPlan ||
            !state.legacyPlan.planInfoData.isCreatePlan
          ) {
            state.legacyPlan.planEntryFormTabValue.previousValue =
              state.legacyPlan.planEntryFormTabValue.value
            state.legacyPlan.planEntryFormTabValue.value = TABS.REVIEW_TAB
          }

          if (
            newParentInfo &&
            !location.pathname.includes('pre-populate-plan')
          ) {
            state.legacyPlan.parentInfo = newParentInfo
          }
          state.legacyPlan.primaryElevationPlan = {
            additionalOptionsData,
            additionalPlanAttributes,
            isCeilingHeightsRequired: planInfoData.isCeilingHeightsRequired,
            planMasterData,
            specifications,
            supplementalDocuments,
          }

          state.legacyPlan.multiElevationPlans[planInfoData.planId[0]] =
            initialElevationPlanData
        }
        if (action.payload?.planId) {
          const {
            additionalOptionsData,
            additionalPlanAttributes,
            parentInfo: newParentInfo,
            planInfoData,
            planMasterData,
            specifications,
            supplementalDocuments,
          } = toPlanToView(action.payload as unknown as PlanType)
          state.draftPlan.isLegacyPlan = isLegacyDraftPlan
          if (state.draftPlan.isEditPlan) {
            state.draftPlan.planInfoData = {
              ...state.draftPlan.planInfoData,
              ...planInfoData,
            }
            state.draftPlan.planEntryFormElevationIndex.id =
              planInfoData.planId[0]

            state.draftPlan.isCeilingHeightsRequired =
              planInfoData?.isCeilingHeightsRequired ?? false

            state.draftPlan.hidden = planInfoData?.hidden ?? false

            recordLennarUrl(
              state,
              planInfoData.planId[0],
              (action.payload as unknown as PlanType)?.lennarUrl
            )
          }
          if (
            state.draftPlan.isEditPlan ||
            !state.draftPlan.planInfoData.isCreatePlan
          ) {
            state.draftPlan.planEntryFormTabValue.previousValue =
              state.draftPlan.planEntryFormTabValue.value
            state.draftPlan.planEntryFormTabValue.value = TABS.REVIEW_TAB
          }

          if (
            newParentInfo &&
            !location.pathname.includes('pre-populate-plan')
          ) {
            state.draftPlan.parentInfo = newParentInfo
          }
          state.draftPlan.primaryElevationPlan = {
            additionalOptionsData,
            additionalPlanAttributes,
            isCeilingHeightsRequired: planInfoData.isCeilingHeightsRequired,
            planMasterData,
            specifications,
            supplementalDocuments,
          }

          state.draftPlan.multiElevationPlans[planInfoData.planId[0]] =
            initialElevationPlanData
        }
        // JDE price/status wins over the stored document values regardless of
        // which response arrived first.
        applyJdePriceStatusOverlay(state)
      })
      .addCase(getPlanDataByPlanId.rejected, (state) => {
        state.draftPlan.isLoading = false
      })
      .addCase(uploadFileForAutoExtract.pending, (state) => {
        state.draftPlan.isLoading = true
        state.draftPlan.hasError = false
      })
      .addCase(uploadFileForAutoExtract.fulfilled, (state) => {
        state.draftPlan.isLoading = false
        state.draftPlan.hasError = false
      })
      .addCase(uploadFileForAutoExtract.rejected, (state, action) => {
        state.draftPlan.isLoading = false
        state.draftPlan.hasError = true
        state.draftPlan.errorDetails =
          action.error.message ?? 'Failed to process file for auto extract'
      })
      .addCase(getPlanSubset.pending, (state) => {
        state.draftPlan.hasError = false
        state.draftPlan.isLoading = true
      })
      .addCase(getPlanSubset.fulfilled, (state, action) => {
        const id = state.draftPlan.planEntryFormElevationIndex.id
        const supportingDocuments = action.payload?.supportingDocuments || []
        const primaryPlanId = state.draftPlan.planInfoData.planId?.[0]

        // TODO: adding this comment for reviewer's attention that this was not related code
        // because getPlanSubset is only for Single- Family Plan. Will remove this comment in subsequent changes

        const activePlan = state.draftPlan.multiElevationPlans[id]
        // Capture old docs BEFORE updating for propagation
        const oldPrimaryDocs =
          state.draftPlan.multiElevationPlans[primaryPlanId]
            ?.supplementalDocuments || []
        if (activePlan) {
          activePlan.supplementalDocuments = supportingDocuments
          state.draftPlan.hasError = false
          state.draftPlan.isLoading = false
        }
        state.draftPlan.planInfoData._etag = action.payload?._etag

        // Only update primaryElevationPlan when working with the primary elevation
        if (id === primaryPlanId) {
          state.draftPlan.primaryElevationPlan.supplementalDocuments =
            supportingDocuments
        }

        // Document propagation for bulk edit
        propagateDocumentsInBulkEdit(
          state,
          id,
          supportingDocuments,
          state.draftPlan.multiElevationPlans
        )

        if (
          !state.draftPlan.isBulkEdit &&
          id === primaryPlanId &&
          state.draftPlan.planInfoData?.planId?.length > 1
        ) {
          propagateDocumentChangesInMultiElevation(
            state,
            oldPrimaryDocs,
            supportingDocuments,
            state.draftPlan.multiElevationPlans
          )
        }
      })
      .addCase(getPlanSubset.rejected, (state, action) => {
        state.draftPlan.hasError = true
        state.draftPlan.errorDetails =
          action.error.message ?? 'Error fetching plans after file upload'
        state.draftPlan.isLoading = false
      })
      .addCase(getShellPlan.pending, (state) => {
        state.draftPlan.hasError = false
        state.draftPlan.isLoading = true
      })
      .addCase(getShellPlan.fulfilled, (state, action) => {
        state.draftPlan.hasError = false
        state.draftPlan.isLoading = false
        const isLegacyDraftPlan =
          (action.payload?.legacyShell?.isLegacyPlan &&
            !action.payload?.submitted) ||
          false
        if (action.payload?.communityPlanId) {
          const {
            _etag,
            communityPlanId,
            garages,
            hidden,
            isLegacyPlan,
            parentInfo,
            plan,
            planCreatedBy,
            planCreatedDateTime,
            shellArea,
            shellOverview,
            shellStructure,
            specifications,
            structureSpecifications,
            submitted,
            supportingDocuments,
          } = action.payload

          if (
            !location.pathname.includes('copy-shell') &&
            !location.pathname.includes('add-shell-elevation') &&
            !location.pathname.includes('pre-populate-shell')
          ) {
            state.draftPlan.planInfoData = {
              ...state.draftPlan.planInfoData,
              _etag,
              buildingType: buildingTypes.SHELL,
              communityData: getCommunityData(shellOverview),
              communityNumber: getCommunityData(shellOverview),
              communityPlanId,
              divisionCode: shellOverview.division,
              familyType: familyTypes.MULTI,
              plan,
              planCategory: shellOverview.shellCategory,
              planElevation: [
                {
                  error: false,
                  label: '',
                  success: true,
                  value: shellOverview.shellElevation,
                },
              ],
              planId: [communityPlanId],
              region: shellOverview.region,
              structureType: shellOverview.structureType,
            }
            if (
              location.pathname.includes('/draft') ||
              location.pathname.includes('/edit-plan')
            ) {
              state.draftPlan.parentInfo = parentInfo
            }
          } else if (!location.pathname.includes('pre-populate-shell')) {
            state.draftPlan.parentInfo = parentInfo
          }

          state.draftPlan.planEntryFormElevationIndex.id =
            state.draftPlan.planInfoData.planId[0]

          state.draftPlan.primaryElevationShellPlan = {
            ...state.draftPlan.primaryElevationShellPlan,
            _etag,
            communityPlanId,
            garages,
            hidden,
            isLegacyPlan,
            planCreatedBy,
            planCreatedDateTime,
            shellArea,
            shellOverview,
            shellStructure,
            specifications,
            structureSpecifications,
            submitted,
          }

          const id = state.draftPlan.planEntryFormElevationIndex.id
          const activePlan = state.draftPlan.multiElevationShellPlans[id]
          if (activePlan) {
            activePlan.supplementalDocuments =
              action.payload?.supportingDocuments || []
            state.draftPlan.hasError = false
            state.draftPlan.isLoading = false
          }

          state.draftPlan.multiElevationShellPlans[
            state.draftPlan.planInfoData.planId[0]
          ] = initialElevationShellPlanData
          state.draftPlan.planInfoData._etag = _etag
          state.draftPlan.primaryElevationShellPlan.supplementalDocuments =
            supportingDocuments
          state.draftPlan.isLegacyPlan = isLegacyDraftPlan
        }
        if (action.payload?.legacyShell?.communityPlanId) {
          const {
            _etag,
            communityPlanId,
            garages,
            hidden,
            parentInfo,
            plan,
            planCreatedBy,
            planCreatedDateTime,
            shellArea,
            shellOverview,
            shellStructure,
            specifications,
            structureSpecifications,
            submitted,
            supportingDocuments,
          } = (action.payload?.legacyShell || {}) as ShellPlanResponse

          if (
            !location.pathname.includes('copy-shell') &&
            !location.pathname.includes('add-shell-elevation') &&
            !location.pathname.includes('pre-populate-shell')
          ) {
            state.legacyPlan.planInfoData = {
              ...state.draftPlan.planInfoData,
              _etag,
              buildingType: buildingTypes.SHELL,
              communityData: getCommunityData(shellOverview),
              communityNumber: getCommunityData(shellOverview),
              communityPlanId,
              divisionCode: shellOverview.division,
              familyType: familyTypes.MULTI,
              plan,
              planCategory: shellOverview.shellCategory,
              planElevation: [
                {
                  error: false,
                  label: '',
                  success: true,
                  value: shellOverview.shellElevation,
                },
              ],
              planId: [communityPlanId],
              region: shellOverview.region,
              structureType: shellOverview.structureType,
            }
            if (
              location.pathname.includes('/draft') ||
              location.pathname.includes('/edit-plan')
            ) {
              state.legacyPlan.parentInfo = parentInfo
            }
          } else if (!location.pathname.includes('pre-populate-shell')) {
            state.legacyPlan.parentInfo = parentInfo
          }

          state.legacyPlan.planEntryFormElevationIndex.id =
            state.legacyPlan.planInfoData.planId[0]

          state.legacyPlan.primaryElevationShellPlan = {
            ...state.legacyPlan.primaryElevationShellPlan,
            _etag,
            communityPlanId,
            garages,
            hidden,
            planCreatedBy,
            planCreatedDateTime,
            shellArea,
            shellOverview,
            shellStructure,
            specifications,
            structureSpecifications,
            submitted,
          }

          const id = state.legacyPlan.planEntryFormElevationIndex.id
          const activePlan = state.legacyPlan.multiElevationShellPlans[id]
          if (activePlan) {
            activePlan.supplementalDocuments =
              action.payload?.supportingDocuments || []
            state.legacyPlan.hasError = false
            state.legacyPlan.isLoading = false
          }

          state.legacyPlan.multiElevationShellPlans[
            state.legacyPlan.planInfoData.planId[0]
          ] = initialElevationShellPlanData
          state.legacyPlan.planInfoData._etag = _etag
          state.legacyPlan.primaryElevationShellPlan.supplementalDocuments =
            supportingDocuments
        }
      })
      .addCase(getShellPlan.rejected, (state, action) => {
        state.draftPlan.hasError = true
        state.draftPlan.errorDetails =
          action.error.message ?? 'Error fetching shell after file upload'
        state.draftPlan.isLoading = false
      })
      .addCase(getUnitPlan.pending, (state, action) => {
        state.draftPlan.hasError = false
        state.draftPlan.isLoading = true
        if (
          state.jdePriceStatus &&
          state.jdePriceStatus.communityPlanId !== action.meta.arg
        ) {
          state.jdePriceStatus = null
        }
      })
      .addCase(getUnitPlan.fulfilled, (state, action) => {
        state.draftPlan.hasError = false
        state.draftPlan.isLoading = false
        if (action.payload?.communityPlanId) {
          const {
            _etag,
            communityPlanId,
            garages,
            isLegacyPlan,
            parentInfo,
            plan,
            planCreatedBy,
            planCreatedDateTime,
            specifications,
            structureSpecifications,
            submitted,
            supportingDocuments,
            unitAdditionalFacilities,
            unitAdditionalRooms,
            unitArea,
            unitOverview,
            unitPrice,
            unitStructure,
          } = action.payload
          state.draftPlan.isLegacyPlan = isLegacyPlan ?? false
          if (
            !location.pathname.includes('copy-unit') &&
            !location.pathname.includes('add-unit-elevation') &&
            !location.pathname.includes('pre-populate-unit')
          ) {
            state.draftPlan.planInfoData = {
              ...state.draftPlan.planInfoData,
              _etag,
              buildingType: buildingTypes.UNIT,
              communityData: getCommunityData(unitOverview),
              communityNumber: getCommunityData(unitOverview),
              communityPlanId,
              divisionCode: unitOverview.division,
              familyType: familyTypes.MULTI,
              plan,
              planCategory: unitOverview.unitCategory,
              planElevation: [
                {
                  error: false,
                  label: '',
                  success: true,
                  value: unitOverview.unitElevation,
                },
              ],
              planId: [communityPlanId],
              region: unitOverview.region,
              structureType: unitOverview.structureType,
            }
            if (
              location.pathname.includes('/draft') ||
              location.pathname.includes('/edit-plan')
            ) {
              state.draftPlan.parentInfo = parentInfo
            }
          } else if (!location.pathname.includes('pre-populate-unit')) {
            state.draftPlan.parentInfo = parentInfo
          }

          state.draftPlan.planEntryFormElevationIndex.id =
            state.draftPlan.planInfoData.planId[0]

          state.draftPlan.primaryElevationUnitPlan = {
            ...state.draftPlan.primaryElevationUnitPlan,
            _etag,
            communityPlanId,
            garages,
            planCreatedBy,
            planCreatedDateTime,
            specifications,
            structureSpecifications,
            submitted,
            unitAdditionalFacilities,
            unitAdditionalRooms,
            unitArea,
            unitOverview,
            unitPrice,
            unitStructure,
          }

          const id = state.draftPlan.planEntryFormElevationIndex.id
          const activePlan = state.draftPlan.multiElevationUnitPlans[id]
          if (activePlan) {
            activePlan.supplementalDocuments =
              action.payload?.supportingDocuments || []
            state.draftPlan.hasError = false
            state.draftPlan.isLoading = false
          }
          state.draftPlan.multiElevationUnitPlans[
            state.draftPlan.planInfoData.planId[0]
          ] = { ...initialElevationUnitPlanData, unitPrice }
          state.draftPlan.planInfoData._etag = _etag
          state.draftPlan.primaryElevationUnitPlan.supplementalDocuments =
            supportingDocuments
        }

        if (action.payload?.legacyUnit?.communityPlanId) {
          const {
            _etag,
            communityPlanId,
            garages,
            parentInfo,
            plan,
            planCreatedBy,
            planCreatedDateTime,
            specifications,
            structureSpecifications,
            submitted,
            supportingDocuments,
            unitAdditionalFacilities,
            unitAdditionalRooms,
            unitArea,
            unitOverview,
            unitPrice,
            unitStructure,
          } = action.payload.legacyUnit as UnitPlanResponse

          if (
            !location.pathname.includes('copy-unit') &&
            !location.pathname.includes('add-unit-elevation') &&
            !location.pathname.includes('pre-populate-unit')
          ) {
            state.legacyUnit.planInfoData = {
              ...state?.draftPlan?.planInfoData,
              _etag,
              buildingType: buildingTypes.UNIT,
              communityData: getCommunityData(unitOverview),
              communityNumber: getCommunityData(unitOverview),
              communityPlanId,
              divisionCode: unitOverview.division,
              familyType: familyTypes.MULTI,
              plan,
              planCategory: unitOverview.unitCategory,
              planElevation: [
                {
                  error: false,
                  label: '',
                  success: true,
                  value: unitOverview.unitElevation,
                },
              ],
              planId: [communityPlanId],
              region: unitOverview.region,
              structureType: unitOverview.structureType,
            }
            if (
              location.pathname.includes('/draft') ||
              location.pathname.includes('/edit-plan')
            ) {
              state.legacyUnit.parentInfo = parentInfo
            }
          } else if (!location.pathname.includes('pre-populate-unit')) {
            state.legacyUnit.parentInfo = parentInfo
          }

          state.legacyUnit.planEntryFormElevationIndex.id =
            state.legacyUnit.planInfoData.planId[0]

          state.legacyUnit.primaryElevationUnitPlan = {
            ...state.legacyUnit.primaryElevationUnitPlan,
            _etag,
            communityPlanId,
            garages,
            planCreatedBy,
            planCreatedDateTime,
            specifications,
            structureSpecifications,
            submitted,
            unitAdditionalFacilities,
            unitAdditionalRooms,
            unitArea,
            unitOverview,
            unitPrice,
            unitStructure,
          }

          const id = state.legacyUnit.planEntryFormElevationIndex.id
          const activePlan = state.legacyUnit.multiElevationUnitPlans[id]
          if (activePlan) {
            activePlan.supplementalDocuments =
              action.payload?.supportingDocuments || []
            state.legacyUnit.hasError = false
            state.legacyUnit.isLoading = false
          }
          state.legacyUnit.multiElevationUnitPlans[
            state.legacyUnit.planInfoData.planId[0]
          ] = { ...initialElevationUnitPlanData, unitPrice }
          state.legacyUnit.planInfoData._etag = _etag
          state.legacyUnit.primaryElevationUnitPlan.supplementalDocuments =
            supportingDocuments
        }
        // JDE price/status wins over the stored document values regardless of
        // which response arrived first.
        applyJdePriceStatusOverlay(state)
      })
      .addCase(getUnitPlan.rejected, (state, action) => {
        state.draftPlan.hasError = true
        state.draftPlan.errorDetails =
          action.error.message ?? 'Error fetching unit after file upload'
      })
      .addCase(getShellPlanSupplementDocuments.pending, (state) => {
        state.draftPlan.hasError = false
        state.draftPlan.isLoading = true
      })
      .addCase(getShellPlanSupplementDocuments.fulfilled, (state, action) => {
        const id = state.draftPlan.planEntryFormElevationIndex.id
        const supportingDocuments = action.payload?.supportingDocuments || []
        const primaryPlanId = state.draftPlan.planInfoData?.planId?.[0]
        const activePlan = state.draftPlan.multiElevationShellPlans[id]
        // Capture old docs BEFORE updating for propagation
        const oldPrimaryDocs =
          state.draftPlan.multiElevationShellPlans[primaryPlanId]
            ?.supplementalDocuments || []

        if (activePlan) {
          activePlan.supplementalDocuments = supportingDocuments
        }
        state.draftPlan.planInfoData._etag = action.payload?._etag
        state.draftPlan.primaryElevationShellPlan._etag = action.payload?._etag
        if (id === primaryPlanId) {
          state.draftPlan.primaryElevationShellPlan.supplementalDocuments =
            supportingDocuments
        }

        state.draftPlan.hasError = false
        state.draftPlan.isLoading = false

        // Document propagation for bulk edit
        propagateDocumentsInBulkEdit(
          state,
          id,
          supportingDocuments,
          state.draftPlan.multiElevationShellPlans
        )

        if (
          !state.draftPlan.isBulkEdit &&
          id === primaryPlanId &&
          state.draftPlan.planInfoData?.planId?.length > 1
        ) {
          propagateDocumentChangesInMultiElevation(
            state,
            oldPrimaryDocs,
            supportingDocuments,
            state.draftPlan.multiElevationShellPlans
          )
        }
      })
      .addCase(getShellPlanSupplementDocuments.rejected, (state, action) => {
        state.draftPlan.hasError = true
        state.draftPlan.errorDetails =
          action.error.message ?? 'Error fetching shell after file upload'
        state.draftPlan.isLoading = false
      })
      .addCase(getShellPlanCopy.pending, (state) => {
        state.draftPlan.hasError = false
        state.draftPlan.isLoading = true
      })
      .addCase(getShellPlanCopy.fulfilled, (state, action) => {
        state.draftPlan.hasError = false
        state.draftPlan.isLoading = false

        const {
          _etag,
          communityPlanId,
          garages,
          parentInfo,
          planName,
          shellArea,
          shellOverview,
          shellStructure,
          specifications,
          structureSpecifications,
          submitted,
          supportingDocuments,
          versionId,
        } = action.payload

        state.draftPlan.primaryElevationShellPlan = {
          ...state.draftPlan.primaryElevationShellPlan,
          _etag,
          communityPlanId,
          garages,
          shellArea,
          shellOverview,
          shellStructure,
          specifications,
          structureSpecifications,
          submitted,
        }

        state.draftPlan.parentInfo = createParentInfo(
          location.pathname,
          'shell',
          parentInfo,
          communityPlanId,
          versionId,
          planName
        )

        const id = state.draftPlan.planEntryFormElevationIndex.id
        const activePlan = state.draftPlan.multiElevationShellPlans[id]
        if (activePlan) {
          activePlan.supplementalDocuments =
            action.payload?.supportingDocuments || []
          state.draftPlan.hasError = false
          state.draftPlan.isLoading = false
        }
        state.draftPlan.planInfoData._etag = _etag
        state.draftPlan.primaryElevationShellPlan.supplementalDocuments =
          supportingDocuments
      })
      .addCase(getShellPlanCopy.rejected, (state, action) => {
        state.draftPlan.hasError = true
        state.draftPlan.errorDetails =
          action.error.message ?? 'Error fetching shell copy'
        state.draftPlan.isLoading = false
      })
      .addCase(getUnitPlanSupplementDocuments.pending, (state) => {
        state.draftPlan.hasError = false
        state.draftPlan.isLoading = true
      })
      .addCase(getUnitPlanSupplementDocuments.fulfilled, (state, action) => {
        const id = state.draftPlan.planEntryFormElevationIndex.id
        const supportingDocuments = action.payload?.supportingDocuments || []
        const primaryPlanId = state.draftPlan.planInfoData?.planId?.[0]
        const activePlan = state.draftPlan.multiElevationUnitPlans[id]
        // Capture old docs BEFORE updating for propagation
        const oldPrimaryDocs =
          state.draftPlan.multiElevationUnitPlans[primaryPlanId]
            ?.supplementalDocuments || []

        if (activePlan) {
          activePlan.supplementalDocuments = supportingDocuments
        }
        state.draftPlan.planInfoData._etag = action.payload?._etag
        state.draftPlan.primaryElevationUnitPlan._etag = action.payload?._etag

        // Only update primaryElevationUnitPlan when on primary elevation (first planId)
        if (id === primaryPlanId) {
          state.draftPlan.primaryElevationUnitPlan.supplementalDocuments =
            supportingDocuments
        }

        state.draftPlan.hasError = false
        state.draftPlan.isLoading = false

        // Document propagation for bulk edit
        propagateDocumentsInBulkEdit(
          state,
          id,
          supportingDocuments,
          state.draftPlan.multiElevationUnitPlans
        )

        if (
          !state.draftPlan.isBulkEdit &&
          id === primaryPlanId &&
          state.draftPlan.planInfoData?.planId?.length > 1
        ) {
          propagateDocumentChangesInMultiElevation(
            state,
            oldPrimaryDocs,
            supportingDocuments,
            state.draftPlan.multiElevationUnitPlans
          )
        }
      })
      .addCase(getUnitPlanSupplementDocuments.rejected, (state, action) => {
        state.draftPlan.hasError = true
        state.draftPlan.errorDetails =
          action.error.message ?? 'Error fetching unit after file upload'
        state.draftPlan.isLoading = false
      })
      .addCase(getUnitPlanCopy.pending, (state) => {
        state.draftPlan.hasError = false
        state.draftPlan.isLoading = true
      })
      .addCase(getUnitPlanCopy.fulfilled, (state, action) => {
        state.draftPlan.hasError = false
        state.draftPlan.isLoading = false

        const {
          _etag,
          communityPlanId,
          garages,
          parentInfo,
          planName,
          specifications,
          structureSpecifications,
          submitted,
          supportingDocuments,
          unitAdditionalFacilities,
          unitAdditionalRooms,
          unitArea,
          unitOverview,
          unitPrice,
          unitStructure,
          versionId,
        } = action.payload

        state.draftPlan.primaryElevationUnitPlan = {
          ...state.draftPlan.primaryElevationUnitPlan,
          _etag,
          communityPlanId,
          garages,
          specifications,
          structureSpecifications,
          submitted,
          unitAdditionalFacilities,
          unitAdditionalRooms,
          unitArea,
          unitOverview,
          unitPrice,
          unitStructure,
        }

        state.draftPlan.parentInfo = createParentInfo(
          location.pathname,
          'unit',
          parentInfo,
          communityPlanId,
          versionId,
          planName
        )

        const id = state.draftPlan.planEntryFormElevationIndex.id
        const activePlan = state.draftPlan.multiElevationUnitPlans[id]
        if (activePlan) {
          activePlan.supplementalDocuments =
            action.payload?.supportingDocuments || []
          state.draftPlan.hasError = false
          state.draftPlan.isLoading = false
        }
        state.draftPlan.planInfoData._etag = _etag
        state.draftPlan.primaryElevationUnitPlan.supplementalDocuments =
          supportingDocuments
      })
      .addCase(getUnitPlanCopy.rejected, (state, action) => {
        state.draftPlan.hasError = true
        state.draftPlan.errorDetails =
          action.error.message ?? 'Error fetching unit copy'
        state.draftPlan.isLoading = false
      })
      .addCase(getBulkEditPlans.pending, (state) => {
        state.draftPlan.hasError = false
        state.draftPlan.isLoading = true
      })
      .addCase(getBulkEditPlans.fulfilled, (state, action) => {
        state.draftPlan.hasError = false
        state.draftPlan.isLoading = false

        const plansData = action.payload
        if (!plansData || plansData.length === 0) {
          return
        }

        // Set bulk edit flag
        state.draftPlan.isBulkEdit = true

        const familyType = state.draftPlanModalInfo.familyType
        const buildingType = state.draftPlanModalInfo.buildingType

        // Determine if this is a multi-family plan
        const isMultiFamilyShell =
          familyType === familyTypes.MULTI &&
          buildingType === buildingTypes.SHELL

        // Don't automatically navigate to review tab after bulk document operations
        // Tab navigation should only happen on initial bulk edit setup, not on refresh
        // Keep the current tab when refreshing after document operations
        const isInitialBulkEditSetup = !state.draftPlan.isBulkEdit

        if (isInitialBulkEditSetup) {
          // Set tab to review for initial bulk edit setup
          // Shell plans have 4 tabs (0-3), so review tab is 3
          // Other plans have 5 tabs (0-4), so review tab is 4
          state.draftPlan.planEntryFormTabValue.previousValue =
            state.draftPlan.planEntryFormTabValue.value
          state.draftPlan.planEntryFormTabValue.value = isMultiFamilyShell
            ? 3
            : TABS.REVIEW_TAB
        }

        const isMultiFamilyUnit =
          familyType === familyTypes.MULTI &&
          buildingType === buildingTypes.UNIT

        // Populate bulkEditPlansInfoData with per-plan metadata
        state.draftPlan.bulkEditPlansInfoData = plansData.map((planData) => {
          if (isMultiFamilyShell) {
            const communityData = getCommunityData(planData.shellOverview)
            return {
              _etag: planData._etag,
              buildingType: buildingTypes.SHELL,
              communityData: communityData ?? [],
              communityNumber: communityData ?? [],
              communityPlanId: planData.communityPlanId,
              division: planData.shellOverview.division,
              divisionCode: planData.shellOverview.division,
              familyType: familyTypes.MULTI,
              file: undefined,
              isCreatePlan: false,
              plan: planData.plan,
              planCategory: planData.shellOverview.shellCategory,
              planElevation: {
                error: false,
                label: planData.shellOverview.shellElevation,
                success: true,
                value: planData.shellOverview.shellElevation,
              },
              planId: planData.communityPlanId,
              region: planData.shellOverview.region,
              structureType: planData.shellOverview.structureType,
              submitted: planData.submitted,
              supplementalDocuments: planData.supplementalDocuments,
            }
          } else if (isMultiFamilyUnit) {
            const communityData = getCommunityData(planData.unitOverview)
            return {
              _etag: planData._etag,
              buildingType: buildingTypes.UNIT,
              communityData: communityData ?? [],
              communityNumber: communityData ?? [],
              communityPlanId: planData.communityPlanId,
              division: planData.unitOverview.division,
              divisionCode: planData.unitOverview.division,
              familyType: familyTypes.MULTI,
              file: undefined,
              isCreatePlan: false,
              plan: planData.plan,
              planCategory: planData.unitOverview.unitCategory,
              planElevation: {
                error: false,
                label: planData.unitOverview.unitElevation,
                success: true,
                value: planData.unitOverview.unitElevation,
              },
              planId: planData.communityPlanId,
              region: planData.unitOverview.region,
              structureType: planData.unitOverview.structureType,
              submitted: planData.submitted,
              supplementalDocuments: planData.supplementalDocuments,
            }
          } else {
            // Single family plan - use toPlanToView to transform the data
            const { planInfoData } = toPlanToView(
              planData as unknown as PlanType
            )
            return {
              _etag: planInfoData._etag,
              buildingType: '',
              communityData: planInfoData.communityData ?? [],
              communityNumber: planInfoData.communityNumber ?? [],
              communityPlanId: planInfoData.communityPlanId,
              division: planData.division,
              divisionCode: planInfoData.divisionCode,
              familyType: familyTypes.SINGLE,
              file: undefined,
              isCreatePlan: false,
              plan: planInfoData.plan,
              planCategory: planInfoData.planCategory,
              planElevation: planInfoData.planElevation?.[0] ?? {
                error: false,
                label: '',
                success: true,
                value: '',
              },
              planId: planInfoData.planId?.[0] ?? planData.planId,
              region: planInfoData.region,
              structureType: planData.planStructure?.foundationType ?? '',
              submitted: planInfoData.submitted,
              supplementalDocuments: planData.supplementalDocuments,
            }
          }
        })

        // Process first plan (base plan) to set planInfoData
        if (isMultiFamilyShell) {
          const firstPlan = plansData[0]
          const {
            _etag,
            communityPlanId,
            garages,
            hidden,
            parentInfo,
            plan,
            planCreatedBy,
            planCreatedDateTime,
            shellArea,
            shellOverview,
            shellStructure,
            specifications,
            structureSpecifications,
            submitted,
            supportingDocuments,
          } = firstPlan

          state.draftPlan.planInfoData = {
            ...state.draftPlan.planInfoData,
            _etag,
            buildingType: buildingTypes.SHELL,
            communityData: getCommunityData(shellOverview),
            communityNumber: getCommunityData(shellOverview),
            communityPlanId,
            divisionCode: shellOverview.division,
            familyType: familyTypes.MULTI,
            plan,
            planCategory: shellOverview.shellCategory,
            planElevation: plansData.map((p, idx) => ({
              error: false,
              label: `Plan Elevation ${idx + 1}`,
              success: true,
              value: p.shellOverview.shellElevation,
            })),
            planId: plansData.map((p) => p.communityPlanId),
            region: shellOverview.region,
            structureType: shellOverview.structureType,
            submitted,
          }

          state.draftPlan.planEntryFormElevationIndex.id =
            state.draftPlan.planInfoData.planId[0]
          state.draftPlan.parentInfo = parentInfo
          state.draftPlan.hidden = hidden ?? false

          state.draftPlan.primaryElevationShellPlan = {
            ...state.draftPlan.primaryElevationShellPlan,
            _etag,
            communityPlanId,
            garages,
            hidden,
            planCreatedBy,
            planCreatedDateTime,
            shellArea,
            shellOverview,
            shellStructure,
            specifications,
            structureSpecifications,
            submitted,
            supplementalDocuments: supportingDocuments,
          }

          // Populate all shell plans
          plansData.forEach((planData) => {
            const planId = planData.communityPlanId
            state.draftPlan.multiElevationShellPlans[planId] = {
              _etag: planData._etag,
              communityPlanId: planData.communityPlanId,
              createdByCorporate: false,
              garages: planData.garages,
              parentInfo: planData.parentInfo,
              planCreatedBy: planData.planCreatedBy,
              planCreatedDateTime: planData.planCreatedDateTime,
              shellArea: planData.shellArea,
              shellOverview: planData.shellOverview,
              shellStructure: planData.shellStructure,
              structureSpecifications: planData.structureSpecifications,
              submitted: planData.submitted,
              supplementalDocuments: planData.supportingDocuments || [],
              versionId: planData.versionId || 0,
            }

            // Store initial documents for bulk edit tracking
            state.draftPlan.initialBulkEditDocuments[planId] = [
              ...(planData.supportingDocuments || []),
            ]
          })
        } else if (isMultiFamilyUnit) {
          const firstPlan = plansData[0]
          const {
            _etag,
            communityPlanId,
            garages,
            parentInfo,
            plan,
            planCreatedBy,
            planCreatedDateTime,
            specifications,
            structureSpecifications,
            submitted,
            supportingDocuments,
            unitAdditionalFacilities,
            unitAdditionalRooms,
            unitArea,
            unitOverview,
            unitPrice,
            unitStructure,
          } = firstPlan

          state.draftPlan.planInfoData = {
            ...state.draftPlan.planInfoData,
            _etag,
            buildingType: buildingTypes.UNIT,
            communityData: getCommunityData(unitOverview),
            communityNumber: getCommunityData(unitOverview),
            communityPlanId,
            divisionCode: unitOverview.division,
            familyType: familyTypes.MULTI,
            plan,
            planCategory: unitOverview.unitCategory,
            planElevation: plansData.map((p, idx) => ({
              error: false,
              label: `Plan Elevation ${idx + 1}`,
              success: true,
              value: p.unitOverview.unitElevation,
            })),
            planId: plansData.map((p) => p.communityPlanId),
            region: unitOverview.region,
            structureType: unitOverview.structureType,
            submitted,
          }

          state.draftPlan.planEntryFormElevationIndex.id =
            state.draftPlan.planInfoData.planId[0]
          state.draftPlan.parentInfo = parentInfo
          state.draftPlan.hidden = firstPlan.hidden ?? false

          state.draftPlan.primaryElevationUnitPlan = {
            ...state.draftPlan.primaryElevationUnitPlan,
            _etag,
            communityPlanId,
            garages,
            planCreatedBy,
            planCreatedDateTime,
            specifications,
            structureSpecifications,
            submitted,
            supplementalDocuments: supportingDocuments,
            unitAdditionalFacilities,
            unitAdditionalRooms,
            unitArea,
            unitOverview,
            unitPrice,
            unitStructure,
          }

          // Populate all unit plans
          plansData.forEach((planData) => {
            const planId = planData.communityPlanId
            state.draftPlan.multiElevationUnitPlans[planId] = {
              _etag: planData._etag,
              communityPlanId: planData.communityPlanId,
              createdByCorporate: false,
              garages: planData.garages,
              parentInfo: planData.parentInfo,
              planCreatedBy: planData.planCreatedBy,
              planCreatedDateTime: planData.planCreatedDateTime,
              structureSpecifications: planData.structureSpecifications,
              submitted: planData.submitted,
              supplementalDocuments: planData.supportingDocuments || [],
              unitAdditionalFacilities: planData.unitAdditionalFacilities,
              unitAdditionalRooms: planData.unitAdditionalRooms,
              unitArea: planData.unitArea,
              unitOverview: planData.unitOverview,
              unitPrice: planData.unitPrice,
              unitStructure: planData.unitStructure,
              versionId: planData.versionId || 0,
            }

            // Store initial documents for bulk edit tracking
            state.draftPlan.initialBulkEditDocuments[planId] = [
              ...(planData.supportingDocuments || []),
            ]
          })
        } else {
          // Single family plans
          const firstPlanData = plansData[0]
          const {
            additionalOptionsData,
            additionalPlanAttributes,
            parentInfo: newParentInfo,
            planInfoData,
            planMasterData,
            specifications,
            supplementalDocuments,
          } = toPlanToView(firstPlanData as unknown as PlanType)

          state.draftPlan.planInfoData = {
            ...state.draftPlan.planInfoData,
            ...planInfoData,
            planElevation: plansData.map((p, idx) => ({
              error: false,
              label: `Plan Elevation ${idx + 1}`,
              success: true,
              value: p.planOverview.planElevation,
            })),
            planId: plansData.map((p) => p.planId),
          }

          state.draftPlan.planEntryFormElevationIndex.id =
            state.draftPlan.planInfoData.planId[0]

          state.draftPlan.isCeilingHeightsRequired =
            planInfoData?.isCeilingHeightsRequired ?? false

          state.draftPlan.hidden = planInfoData?.hidden ?? false

          if (newParentInfo) {
            state.draftPlan.parentInfo = newParentInfo
          }

          state.draftPlan.primaryElevationPlan = {
            additionalOptionsData,
            additionalPlanAttributes,
            isCeilingHeightsRequired: planInfoData.isCeilingHeightsRequired,
            planMasterData,
            specifications,
            supplementalDocuments,
          }

          // Populate all single family plans
          plansData.forEach((planData) => {
            const {
              additionalOptionsData: elevAdditionalOptions,
              additionalPlanAttributes: elevAdditionalAttrs,
              planInfoData: elevPlanInfo,
              planMasterData: elevPlanMaster,
              specifications: elevSpecs,
              supplementalDocuments: elevDocs,
            } = toPlanToView(planData as unknown as PlanType)

            const planId = elevPlanInfo.planId[0]
            state.draftPlan.multiElevationPlans[planId] = {
              additionalOptionsData: elevAdditionalOptions,
              additionalPlanAttributes: elevAdditionalAttrs,
              isCeilingHeightsRequired:
                elevPlanInfo.isCeilingHeightsRequired ?? false,
              planMasterData: elevPlanMaster,
              specifications: elevSpecs,
              supplementalDocuments: elevDocs,
            }

            // Store initial documents for bulk edit tracking
            state.draftPlan.initialBulkEditDocuments[planId] = [...elevDocs]
          })
        }
      })
      .addCase(getBulkEditPlans.rejected, (state, action) => {
        state.draftPlan.hasError = true
        state.draftPlan.errorDetails =
          action.error.message ?? 'Error fetching plans for bulk edit'
        state.draftPlan.isLoading = false
      })
  },
  initialState,
  name: 'createPlan',
  reducers: {
    addFieldToProcessedValidationErrors: (state, action) => {
      const index = state.draftPlan.planEntryFormElevationIndex.index
      const fieldDetail = action.payload

      if (index === 0) {
        // Iterate over all processedValidationErrors and apply the logic to each one
        state.draftPlan.processedValidationErrors.forEach(
          (elevationErrorItem: ProcessedValidationErrorItem) => {
            // Check if the field already exists to avoid duplicates
            const existingField = elevationErrorItem?.errorDetails?.find(
              (errorDetail) => errorDetail === fieldDetail
            )

            if (!existingField) {
              elevationErrorItem?.errorDetails?.push(fieldDetail)
            }
          }
        )
      } else {
        const elevationErrorItem = state.draftPlan?.processedValidationErrors[
          index
        ] as ProcessedValidationErrorItem

        if (elevationErrorItem) {
          // Check if the field already exists to avoid duplicates
          const existingField = elevationErrorItem?.errorDetails?.find(
            (errorDetail) => errorDetail === fieldDetail
          )

          if (!existingField) {
            elevationErrorItem?.errorDetails?.push(fieldDetail)
          }
        }
      }
    },
    addOrRemoveDraftCommunities(state, action) {
      if (state.draftPlanModalInfo.communityNumber) {
        if (action.payload.isAdd) {
          const newCommunityNumberObj = cloneDeep(communityNumberObj)
          newCommunityNumberObj.communityNumberLabel =
            'Community Number ' +
            (state.draftPlanModalInfo?.communityNumber?.length + 1)
          state.draftPlanModalInfo.communityNumber.push(newCommunityNumberObj)
        } else {
          state.draftPlanModalInfo.communityNumber.splice(
            action.payload.index,
            1
          )
          state.draftPlanModalInfo.communityNumber.forEach(
            (pe, index) =>
              (pe.communityNumberLabel = 'Community Number ' + (index + 1))
          )
        }
      }
    },
    addOrRemoveDraftPlanElevation(state, action) {
      if (action.payload.isAdd) {
        const newPlanElevationObj = cloneDeep(planElevationObj)
        newPlanElevationObj.label =
          'Plan Elevation ' +
          (Number(state.draftPlanModalInfo.planElevation.length) + 1)
        state.draftPlanModalInfo.planElevation.push(newPlanElevationObj)
      } else {
        state.draftPlanModalInfo.planElevation.splice(action.payload.index, 1)
        state.draftPlanModalInfo.planElevation.forEach(
          (pe, index) => (pe.label = 'Plan Elevation ' + (index + 1))
        )
      }
    },
    patchDraftPlan: (state, action) => {
      state.draftPlan = {
        ...state.draftPlan,
        ...action.payload,
      }
    },
    patchDraftPlanCommunityValue: (state, action) => {
      if (state.draftPlanModalInfo.communityNumber) {
        const selectedCommunity =
          state.draftPlanModalInfo.communityNumber[action.payload.index]
        if (selectedCommunity) {
          state.draftPlanModalInfo.communityNumber[action.payload.index] = {
            ...selectedCommunity,
            ...action.payload.value,
          }
        }
      }
    },
    patchDraftPlanInfo: (state, action) => {
      state.draftPlan.planInfoData = {
        ...state.draftPlan.planInfoData,
        ...action.payload,
      }
      state.draftPlan.planEntryFormElevationIndex.id = action.payload.planId[0]

      const initializeElevationPlans = (
        planIds: string[],
        planMap: Record<string, IPlan | IShellPlan | IUnitPlan>,
        initialPlanData: IPlan | IShellPlan | IUnitPlan
      ) => {
        planIds.forEach((planId: string) => {
          if (isUnsafeObjectKey(planId)) {
            return
          }
          if (!Object.prototype.hasOwnProperty.call(planMap, planId)) {
            planMap[planId] = cloneDeep(initialPlanData)
          }
        })
      }

      const planIds: string[] = action.payload.planId

      initializeElevationPlans(
        planIds,
        state.draftPlan.multiElevationPlans,
        initialElevationPlanData
      )
      initializeElevationPlans(
        planIds,
        state.draftPlan.multiElevationShellPlans,
        initialElevationShellPlanData
      )
      initializeElevationPlans(
        planIds,
        state.draftPlan.multiElevationUnitPlans,
        initialElevationUnitPlanData
      )
    },
    patchDraftPlanInfoForAutoUploadedFile: (state, action) => {
      state.draftPlan.planInfoData.file = action.payload
    },
    patchDraftPlanModal: (state, action) => {
      state.draftPlanModalInfo = {
        ...state.draftPlanModalInfo,
        ...action.payload,
      }
    },
    patchDraftPlanValidationValue: (state, action) => {
      state.draftPlanModalValidation = {
        ...state.draftPlanModalValidation,
        ...action.payload.value,
      }
      if (action.payload.isSubmit && state.draftPlanModalInfo.communityNumber) {
        state.draftPlanModalInfo.communityNumber.forEach((element) => {
          element.cNameShowErrorMsg = !element.cNameVal?.communityName
        })

        state.draftPlanModalInfo.planElevation.forEach((element) => {
          element.error = !element.value
        })
      }
    },
    patchPlanData: <T extends keyof IPlan>(
      state: State,
      action: PatchPlanDataAction<T>
    ) => {
      const id = state.draftPlan.planEntryFormElevationIndex.id
      const index = state.draftPlan.planEntryFormElevationIndex.index
      const activePlan = state.draftPlan.multiElevationPlans[id]
      const isBulkEdit = state.draftPlan.isBulkEdit
      const isBasePlan = index === 0

      if (action.payload.section && activePlan) {
        const targetSection = activePlan[action.payload.section]
        if (targetSection && typeof targetSection === 'object') {
          activePlan[action.payload.section] = {
            ...targetSection,
            ...action.payload.value,
          } as IPlan[T]

          // In bulk edit mode, propagate all attributes from base plan to all other plans
          if (isBulkEdit && isBasePlan) {
            propagateAttributesInBulkEdit(
              state,
              state.draftPlan.multiElevationPlans,
              action.payload.section,
              action.payload.value
            )
          }
        }
      }
    },
    patchShellPlanData: <T extends keyof IShellPlan>(
      state: State,
      action: PatchShellPlanDataAction<T>
    ) => {
      const id = state.draftPlan.planEntryFormElevationIndex.id
      const index = state.draftPlan.planEntryFormElevationIndex.index
      const activePlan = state.draftPlan.multiElevationShellPlans[id]
      const isBulkEdit = state.draftPlan.isBulkEdit
      const isBasePlan = index === 0

      if (action.payload.section && activePlan) {
        const targetSection = activePlan[action.payload.section]
        if (targetSection && typeof targetSection === 'object') {
          activePlan[action.payload.section] = {
            ...targetSection,
            ...action.payload.value,
          } as IShellPlan[T]

          // In bulk edit mode, propagate all attributes from base plan to all other plans
          if (isBulkEdit && isBasePlan) {
            propagateAttributesInBulkEdit(
              state,
              state.draftPlan.multiElevationShellPlans,
              action.payload.section,
              action.payload.value
            )
          }
        }
      }
    },
    patchUnitPlanData: <T extends keyof IUnitPlan>(
      state: State,
      action: PatchUnitPlanDataAction<T>
    ) => {
      const id = state.draftPlan.planEntryFormElevationIndex.id
      const index = state.draftPlan.planEntryFormElevationIndex.index
      const activePlan = state.draftPlan.multiElevationUnitPlans[id]
      const isBulkEdit = state.draftPlan.isBulkEdit
      const isBasePlan = index === 0

      if (action.payload.section && activePlan) {
        const targetSection = activePlan[action.payload.section]
        if (targetSection && typeof targetSection === 'object') {
          activePlan[action.payload.section] = {
            ...targetSection,
            ...action.payload.value,
          } as IUnitPlan[T]

          // In bulk edit mode, propagate all attributes from base plan to all other plans
          if (isBulkEdit && isBasePlan) {
            propagateAttributesInBulkEdit(
              state,
              state.draftPlan.multiElevationUnitPlans,
              action.payload.section,
              action.payload.value
            )
          }
        }
      }
    },
    removeFieldFromProcessedValidationErrors: (state, action) => {
      const index = state.draftPlan.planEntryFormElevationIndex.index
      const field = action.payload

      if (index === 0) {
        // Iterate over all processedValidationErrors and apply the filtering logic to each one
        state.draftPlan.processedValidationErrors.forEach(
          (elevationErrorItem: ProcessedValidationErrorItem) => {
            elevationErrorItem.errorDetails =
              elevationErrorItem?.errorDetails?.filter(
                (errorDetail) => errorDetail !== field
              )
          }
        )
      } else {
        const elevationErrorItem = state.draftPlan?.processedValidationErrors[
          index
        ] as ProcessedValidationErrorItem

        if (elevationErrorItem) {
          elevationErrorItem.errorDetails =
            elevationErrorItem?.errorDetails?.filter(
              (errorDetail) => errorDetail !== field
            )
        }
      }
    },
    removeSingleFamilyPlanKey: <T extends keyof IPlan>(
      state: State,
      action: RemovePlanKeyAction<T>
    ) => {
      const id = state.draftPlan.planEntryFormElevationIndex.id
      const activePlan = state.draftPlan.multiElevationPlans[id]

      if (action.payload.section && activePlan) {
        const targetSection = activePlan[action.payload.section]
        if (targetSection && typeof targetSection === 'object') {
          delete (targetSection as Record<string, unknown>)[action.payload.key]
        }
      }
    },
    /**
     * LTGC-5711: an approver may approve a division's copy of an Approved
     * Regional Plan only under a NEW plan number. The plan number is part of the
     * document id (community + plan + elevation), so a rename cannot update the
     * draft in place — the submit has to write a new document, and the caller
     * deletes the old draft afterwards.
     *
     * Everything keyed by the old id therefore has to move with it. The elevation
     * data is read by id when the payload is assembled (CreatePlan
     * getPlansToSave), so leaving it behind would submit an empty plan under the
     * new number; and an etag carried over would turn the create into a
     * conditional update against a document that does not exist yet.
     */
    renameRegionalPlanNumber: (
      state,
      action: { payload: { planIds: string[]; planNumber: string } }
    ) => {
      const { planIds, planNumber } = action.payload
      const previousPlanIds = state.draftPlan.planInfoData?.planId ?? []

      // All or nothing. Applying a partial rename would leave planInfoData
      // naming ids that nothing is stored under — a draft that submits empty —
      // so a payload that cannot be applied in full is refused outright rather
      // than half-written.
      const isRenamable =
        planIds.length === previousPlanIds.length &&
        planIds.every((planId) => Boolean(planId) && !isUnsafeObjectKey(planId))
      if (!isRenamable) {
        return
      }

      previousPlanIds.forEach((previousPlanId, index) => {
        const nextPlanId = planIds[index]
        if (nextPlanId === previousPlanId) {
          return
        }

        const elevationPlan =
          state.draftPlan.multiElevationPlans[previousPlanId]
        if (elevationPlan) {
          state.draftPlan.multiElevationPlans[nextPlanId] = elevationPlan
          delete state.draftPlan.multiElevationPlans[previousPlanId]
        }

        // Etags are tracked lowercased (see CreatePlan getPlanEtag).
        delete state.draftPlan.eTagTracker[previousPlanId.toLowerCase()]
        delete state.draftPlan.eTagTracker[nextPlanId.toLowerCase()]

        if (state.draftPlan.planEntryFormElevationIndex.id === previousPlanId) {
          state.draftPlan.planEntryFormElevationIndex.id = nextPlanId
        }
      })

      state.draftPlan.planInfoData = {
        ...state.draftPlan.planInfoData,
        _etag: undefined,
        communityPlanId: planIds[0],
        plan: planNumber,
        planId: planIds,
      }
    },
    resetDraftPlan: (state) => {
      state.draftPlan = initialDraftPlan
    },
    resetDraftPlanModal: (
      state,
      action: { payload: ResetDraftPlanModalPayload }
    ) => {
      state.draftPlanModalInfo = cloneDeep(planModalInitialState)
      state.draftPlanModalValidation = cloneDeep(initialPlanModalValidation)

      const {
        familyType,
        isAddElevation,
        isCopyPlan,
        isForceReset,
        isPrePopulatePlan,
        selectedPlan,
      } = action.payload

      const shouldSetPlanInfo =
        selectedPlan &&
        ((!isForceReset && isCopyPlan) || isAddElevation || isPrePopulatePlan)
      if (shouldSetPlanInfo) {
        state.draftPlanModalInfo.plan = selectedPlan.plan
        state.draftPlanModalInfo.isCreatePlan = false

        // LTGC-5711: copying an Approved Regional Plan always lands on a
        // community plan, and the elevation is chosen in the modal from the
        // ARP's own list rather than pre-filled — the grid row would supply
        // the consolidated 'A, B, C' string, which is not an elevation.
        const isRegionalPlanCopy =
          !isForceReset &&
          isCopyPlan &&
          selectedPlan.planCategory?.toLowerCase() ===
            planCategories.APPROVED_REGIONAL_PLAN
        if (isRegionalPlanCopy) {
          state.draftPlanModalInfo.planCategory = planCategories.COMMUNITY
        }

        if (!isForceReset && (isCopyPlan || isPrePopulatePlan)) {
          const selectedPlanElevation =
            state.draftPlanModalInfo.planElevation.find(
              (elevation) => elevation.label === defaultPlanElevationLabel
            )
          if (selectedPlanElevation && !isRegionalPlanCopy) {
            const elevationValue =
              familyType === familyTypes.SINGLE &&
              'planElevation' in selectedPlan
                ? (selectedPlan as IModifiedPlan).planElevation
                : (selectedPlan as IMultiFamilyGridPlan).elevation
            selectedPlanElevation.value = elevationValue ?? ''
          }
        }

        if (action.payload.isAddElevation) {
          state.draftPlanModalInfo.planElevation =
            state.draftPlanModalInfo.planElevation.map((elevation) => ({
              ...elevation,
              value: '',
            }))

          const communityValue =
            ('communityId' in selectedPlan && selectedPlan.communityId) ||
            ('communityNumber' in selectedPlan &&
              typeof selectedPlan.communityNumber === 'string' &&
              selectedPlan.communityNumber) ||
            ''

          if (communityValue && state.draftPlanModalInfo.communityNumber) {
            state.draftPlanModalInfo.communityNumber[0].cNumberVal =
              communityValue
          }
        }
      }
    },
    resetDraftPlanValidationValue: (state) => {
      state.draftPlanModalValidation = initialPlanModalValidation
    },
    resetElevation(state) {
      state.draftPlan.planEntryFormElevationIndex.index = 0
      state.draftPlan.planEntryFormElevationIndex.previousIndex = 0
    },
    resetPlanModalElevations: (state) => {
      state.draftPlanModalInfo.planElevation = [planElevationObj]
    },
    resetTab(state) {
      state.draftPlan.planEntryFormTabValue.value = 0
      state.draftPlan.planEntryFormTabValue.previousValue = 0
    },
    setAutoExtractedDataProcessed: (state, action) => {
      state.autoExtractedData.successfullyUpdatedDraftPlan =
        action.payload.successfullyUpdatedDraftPlan
      state.autoExtractedData.error = action.payload.error
      state.autoExtractedData.planIds = action.payload.planIds
      state.autoExtractedData.successfulPlans = action.payload.successfulPlans
      state.autoExtractedData.failedPlanIds = action.payload.failedPlanIds
      state.autoExtractedData.fileUploadedBy = action.payload.fileUploadedBy
    },
    setDocumentPropagationEnabled: (state, action) => {
      state.draftPlan.isDocumentPropagationEnabled = action.payload
    },
    setDraftPlan: <K extends keyof IDraftPlan>(
      state: State,
      action: SetDraftPlanAction<K>
    ) => {
      state.draftPlan[action.payload.key] = action.payload.value
    },
    setDraftPlanModalValue: <K extends keyof IPlanModalInfo>(
      state: ICreatePlan,
      action: SetDraftPlanModalValueAction<K>
    ) => {
      if (action.payload.key === 'bypassCorePlanValidation') {
        state.draftPlanModalInfo.bypassCorePlanValidation = action.payload
          .value as boolean
      } else if (
        action.payload.key === 'planCategory' &&
        state.draftPlanModalInfo.familyType === familyTypes.SINGLE
      ) {
        const plan = state.draftPlanModalInfo.plan
        const isCreatePlan = state.draftPlanModalInfo.isCreatePlan
        const familyType = state.draftPlanModalInfo.familyType
        const buildingType = state.draftPlanModalInfo.buildingType
        const selectedPlanElevation =
          state.draftPlanModalInfo.planElevation.find(
            (elevation: IPlanElevation) =>
              elevation.label === defaultPlanElevationLabel
          )
        state.draftPlanModalInfo = cloneDeep(planModalInitialState)
        state.draftPlanModalValidation = cloneDeep(initialPlanModalValidation)
        if (!isCreatePlan) {
          state.draftPlanModalInfo.plan = plan
          state.draftPlanModalInfo.isCreatePlan = false
          state.draftPlanModalInfo.familyType = familyType
          state.draftPlanModalInfo.buildingType = buildingType
          const newSelectedPlanElevation =
            state.draftPlanModalInfo.planElevation.find(
              (elevation: IPlanElevation) =>
                elevation.label === defaultPlanElevationLabel
            )
          if (newSelectedPlanElevation && selectedPlanElevation) {
            newSelectedPlanElevation.value = selectedPlanElevation.value
          }
        }
      } else if (
        action.payload.key === 'planCategory' &&
        state.draftPlanModalInfo.familyType === familyTypes.MULTI
      ) {
        state.draftPlanModalInfo = {
          ...state.draftPlanModalInfo,
          planCategory: String(action.payload.value),
        }
      }
      switch (action.payload.key) {
        case 'planElevation': {
          const selectedPlanElevation =
            state.draftPlanModalInfo.planElevation.find(
              (elevation: IPlanElevation) =>
                elevation.label === action.payload.id
            )
          if (selectedPlanElevation) {
            selectedPlanElevation.value = String(action.payload.value)
          }
          break
        }
        case 'communityNumber': {
          if (state.draftPlanModalInfo.communityNumber) {
            const selectedCommunityArray =
              state.draftPlanModalInfo.communityNumber[
                action.payload.id as number
              ]
            if (selectedCommunityArray) {
              selectedCommunityArray.cNameVal = {}
              selectedCommunityArray.cNumberVal = String(action.payload.value)
            }
          }
          break
        }
        case 'familyType': {
          state.draftPlanModalInfo = {
            ...state.draftPlanModalInfo,
            buildingType: buildingTypes.SHELL, // Resets the building type whenever there is a change in familyType selection
            familyType: String(action.payload.value),
          }
          break
        }
        case 'autofill': {
          state.draftPlanModalInfo = {
            ...state.draftPlanModalInfo,
            autofill: String(action.payload.value),
          }
          break
        }
        case 'structureType': {
          state.draftPlanModalInfo = {
            ...state.draftPlanModalInfo,
            structureType: String(action.payload.value),
          }
          break
        }

        case 'buildingType': {
          state.draftPlanModalInfo = {
            ...state.draftPlanModalInfo,
            buildingType: String(action.payload.value),
            planElevation: state.draftPlanModalInfo.planElevation ?? [
              planElevationObj,
            ],
            structureType:
              action.payload.value === buildingTypes.UNIT &&
              (state.draftPlanModalInfo.structureType ===
                structureTypes.GARAGE ||
                state.draftPlanModalInfo.structureType ===
                  structureTypes.CARPORT)
                ? structureTypes.TOWN_HOME
                : state.draftPlanModalInfo.structureType,
          }
          break
        }
        default:
          state.draftPlanModalInfo[action.payload.key] = action.payload.value
      }
    },
    setElevation(state, action) {
      state.draftPlan.planEntryFormElevationIndex.previousIndex =
        state.draftPlan.planEntryFormElevationIndex.index
      state.draftPlan.planEntryFormElevationIndex.index = action.payload.value
      const newPlanId =
        state.draftPlan.planInfoData.planId[action.payload.value]
      state.draftPlan.planEntryFormElevationIndex.id = newPlanId
      const isPrimaryElevation =
        state.draftPlan.planEntryFormElevationIndex.index === 0
      state.draftPlan.isPrimaryElevationEnabled = isPrimaryElevation
      state.draftPlan.isDocumentPropagationEnabled =
        state.draftPlan.planEntryFormElevationIndex.previousIndex === 0

      // In bulk-edit mode, plans are already fully loaded - don't overwrite with initial data
      const isBulkEdit = state.draftPlan.isBulkEdit
      const shouldInitializePlan = (
        planMap: Record<string, IPlan | IShellPlan | IUnitPlan>
      ) => !isBulkEdit && (!planMap || isEmpty(planMap[newPlanId]))

      if (
        state.draftPlanModalInfo.buildingType === buildingTypes.SHELL &&
        state.draftPlanModalInfo.familyType === familyTypes.MULTI
      ) {
        if (shouldInitializePlan(state.draftPlan.multiElevationShellPlans)) {
          state.draftPlan.multiElevationShellPlans[newPlanId] =
            initialElevationShellPlanData
        }
      } else if (
        state.draftPlanModalInfo.buildingType === buildingTypes.UNIT &&
        state.draftPlanModalInfo.familyType === familyTypes.MULTI
      ) {
        if (shouldInitializePlan(state.draftPlan.multiElevationUnitPlans)) {
          state.draftPlan.multiElevationUnitPlans[newPlanId] =
            initialElevationUnitPlanData
        }
      } else {
        if (shouldInitializePlan(state.draftPlan.multiElevationPlans)) {
          state.draftPlan.multiElevationPlans[newPlanId] =
            initialElevationPlanData
        }
      }

      if (!state.draftPlan.fieldErrors) {
        state.draftPlan.fieldErrors = []
      }

      if (
        !state.draftPlan.fieldErrors ||
        isEmpty(state.draftPlan.fieldErrors[action.payload.value])
      ) {
        state.draftPlan.fieldErrors[action.payload.value] = {} as IPlanError
      }
    },
    setFileVersion: (state, action) => {
      state.draftPlan.fileVersion = {
        ...state.draftPlan.fileVersion,
        ...action.payload,
      }
    },
    setFloorPlanMetadata: (state, action) => {
      state.draftPlan.floorPlanMetadata = {
        ...state.draftPlan.floorPlanMetadata,
        ...action.payload,
      }
    },
    setIsAutoExtractFromDocument: (state, action) => {
      state.draftPlan.isAutoExtractFromDocument = action.payload
    },
    setPlanCategory: (state, action) => {
      state.draftPlanModalInfo.planCategory = action.payload
    },
    setPlanData: <T extends keyof IPlan>(
      state: State,
      action: SetPlanDataAction<T>
    ) => {
      const id = state.draftPlan.planEntryFormElevationIndex.id
      const activePlan = state.draftPlan.multiElevationPlans[id]
      const isBulkEdit = state.draftPlan.isBulkEdit
      const isBasePlan = state.draftPlan.planEntryFormElevationIndex.index === 0

      if (action.payload.section && activePlan) {
        const targetSection = activePlan[action.payload.section]
        if (targetSection && typeof targetSection === 'object') {
          ;(targetSection as Record<string, unknown>)[action.payload.key] =
            action.payload.value

          // For bulk-edit mode: propagate changes from base plan (index 0) to all other plans
          if (isBulkEdit && isBasePlan) {
            const planIds = state.draftPlan.planInfoData?.planId || []

            // Update all other plans (skip index 0 which is already updated above)
            planIds.forEach((planId, index) => {
              if (index > 0) {
                const otherPlan = state.draftPlan.multiElevationPlans[planId]
                if (otherPlan && otherPlan[action.payload.section]) {
                  const otherSection = otherPlan[action.payload.section]
                  if (otherSection && typeof otherSection === 'object') {
                    ;(otherSection as Record<string, unknown>)[
                      action.payload.key
                    ] = action.payload.value
                  }
                }
              }
            })
          }
        }
      } else {
        const draftPrimaryElevationPlan = state.draftPlan.primaryElevationPlan
        const key = action.payload.key

        if (
          draftPrimaryElevationPlan &&
          draftPrimaryElevationPlan[action.payload.section]
        ) {
          ;(
            draftPrimaryElevationPlan[action.payload.section] as Record<
              string,
              unknown
            >
          )[key] = action.payload.value
        }
      }
    },
    setProcessedValidationErrors: (state, action) => {
      state.draftPlan.processedValidationErrors = action.payload
    },
    setShellPlanData: <T extends keyof IShellPlan>(
      state: State,
      action: SetShellPlanDataAction<T>
    ) => {
      const id = state.draftPlan.planEntryFormElevationIndex.id
      const activePlan = state.draftPlan.multiElevationShellPlans[id]
      const isBulkEdit = state.draftPlan.isBulkEdit
      const isBasePlan = state.draftPlan.planEntryFormElevationIndex.index === 0

      if (action.payload.section && activePlan) {
        const targetSection = activePlan[action.payload.section]
        if (targetSection && typeof targetSection === 'object') {
          ;(targetSection as unknown as Record<string, unknown>)[
            action.payload.key
          ] = action.payload.value

          // For bulk-edit mode: propagate changes from base plan (index 0) to all other plans
          if (isBulkEdit && isBasePlan) {
            const planIds = state.draftPlan.planInfoData?.planId || []

            // Update all other plans (skip index 0 which is already updated above)
            planIds.forEach((planId, index) => {
              if (index > 0) {
                const otherPlan =
                  state.draftPlan.multiElevationShellPlans[planId]
                if (otherPlan && otherPlan[action.payload.section]) {
                  const otherSection = otherPlan[action.payload.section]
                  if (otherSection && typeof otherSection === 'object') {
                    ;(otherSection as unknown as Record<string, unknown>)[
                      action.payload.key
                    ] = action.payload.value
                  }
                }
              }
            })
          }
        }
      }
      // In the following else block, we are setting the autofill values.
      else {
        const draftPrimaryElevationPlan =
          state.draftPlan.primaryElevationShellPlan
        const key = action.payload.key

        if (
          draftPrimaryElevationPlan &&
          draftPrimaryElevationPlan[action.payload.section]
        ) {
          ;(
            draftPrimaryElevationPlan[
              action.payload.section
            ] as unknown as Record<string, unknown>
          )[key] = action.payload.value
        }
      }
    },
    setSpecificationsData: (state, action) => {
      const id = state.draftPlan.planEntryFormElevationIndex.id
      if (
        state.draftPlanModalInfo.buildingType === buildingTypes.SHELL &&
        state.draftPlanModalInfo.familyType === familyTypes.MULTI
      ) {
        const activePlan = state.draftPlan.multiElevationShellPlans[id]
        activePlan.specifications = {
          ...activePlan.specifications,
          ...action.payload,
        }
      } else if (
        state.draftPlanModalInfo.buildingType === buildingTypes.UNIT &&
        state.draftPlanModalInfo.familyType === familyTypes.MULTI
      ) {
        const activePlan = state.draftPlan.multiElevationUnitPlans[id]
        activePlan.specifications = {
          ...activePlan.specifications,
          ...action.payload,
        }
      } else {
        const activePlan = state.draftPlan.multiElevationPlans[id]

        activePlan.specifications = {
          ...activePlan.specifications,
          ...action.payload,
        }
      }
    },
    setSupplementalDocumentsData: (state, action) => {
      const { planId, supplementalDocuments } = action.payload
      const currentId = state.draftPlan.planEntryFormElevationIndex.id

      let planMap: Record<string, IPlan | IShellPlan | IUnitPlan>
      let primaryPlanKey:
        | 'primaryElevationPlan'
        | 'primaryElevationShellPlan'
        | 'primaryElevationUnitPlan'

      if (
        state.draftPlanModalInfo.buildingType === buildingTypes.SHELL &&
        state.draftPlanModalInfo.familyType === familyTypes.MULTI
      ) {
        planMap = state.draftPlan.multiElevationShellPlans
        primaryPlanKey = 'primaryElevationShellPlan'
      } else if (
        state.draftPlanModalInfo.buildingType === buildingTypes.UNIT &&
        state.draftPlanModalInfo.familyType === familyTypes.MULTI
      ) {
        planMap = state.draftPlan.multiElevationUnitPlans
        primaryPlanKey = 'primaryElevationUnitPlan'
      } else {
        planMap = state.draftPlan.multiElevationPlans
        primaryPlanKey = 'primaryElevationPlan'
      }

      // Determine which plan to update
      const targetPlanId = planId !== undefined ? planId : currentId
      const primaryPlanId = state.draftPlan.planInfoData?.planId?.[0]
      const activePlan = planMap[targetPlanId]

      // Capture old docs BEFORE updating for propagation
      const oldPrimaryDocs = planMap[primaryPlanId]?.supplementalDocuments || []

      const documentsToSet =
        supplementalDocuments !== undefined
          ? supplementalDocuments
          : Array.isArray(action.payload)
            ? [...action.payload]
            : action.payload

      if (activePlan) {
        activePlan.supplementalDocuments = documentsToSet
      }

      // Also update the primary elevation plan if this is the primary plan (first planId)
      if (targetPlanId === primaryPlanId && state.draftPlan[primaryPlanKey]) {
        state.draftPlan[primaryPlanKey].supplementalDocuments = documentsToSet
      }

      // Bulk edit document propagation: propagate new documents from base plan to other plans
      if (
        state.draftPlan.isBulkEdit &&
        targetPlanId === primaryPlanId &&
        state.draftPlan.planInfoData?.planId
      ) {
        propagateDocumentChangesInBulkEdit(
          state,
          targetPlanId,
          documentsToSet,
          planMap
        )
      }

      if (
        !state.draftPlan.isBulkEdit &&
        targetPlanId === primaryPlanId &&
        state.draftPlan.planInfoData?.planId?.length > 1
      ) {
        propagateDocumentChangesInMultiElevation(
          state,
          oldPrimaryDocs,
          documentsToSet,
          planMap
        )
      }
    },
    setTabValue: (state, action) => {
      state.draftPlan.planEntryFormTabValue.previousValue =
        state.draftPlan.planEntryFormTabValue.value
      state.draftPlan.planEntryFormTabValue.value = action.payload.value
    },
    setUnitPlanData: <T extends keyof IUnitPlan>(
      state: State,
      action: SetUnitPlanDataAction<T>
    ) => {
      const id = state.draftPlan.planEntryFormElevationIndex.id
      const activePlan = state.draftPlan.multiElevationUnitPlans[id]
      const isBulkEdit = state.draftPlan.isBulkEdit
      const isBasePlan = state.draftPlan.planEntryFormElevationIndex.index === 0

      if (action.payload.section && activePlan) {
        const targetSection = activePlan[action.payload.section]
        if (targetSection && typeof targetSection === 'object') {
          ;(targetSection as unknown as Record<string, unknown>)[
            action.payload.key
          ] = action.payload.value

          // For bulk-edit mode: propagate changes from base plan (index 0) to all other plans
          if (isBulkEdit && isBasePlan) {
            const planIds = state.draftPlan.planInfoData?.planId || []

            // Update all other plans (skip index 0 which is already updated above)
            planIds.forEach((planId, index) => {
              if (index > 0) {
                const otherPlan =
                  state.draftPlan.multiElevationUnitPlans[planId]
                if (otherPlan && otherPlan[action.payload.section]) {
                  const otherSection = otherPlan[action.payload.section]
                  if (otherSection && typeof otherSection === 'object') {
                    ;(otherSection as unknown as Record<string, unknown>)[
                      action.payload.key
                    ] = action.payload.value
                  }
                }
              }
            })
          }
        }
      }
      // In the following else block, we are setting the autofill values.
      else {
        const draftPrimaryElevationPlan =
          state.draftPlan.primaryElevationUnitPlan
        const key = action.payload.key

        if (
          draftPrimaryElevationPlan &&
          draftPrimaryElevationPlan[action.payload.section]
        ) {
          ;(
            draftPrimaryElevationPlan[
              action.payload.section
            ] as unknown as Record<string, unknown>
          )[key] = action.payload.value
        }
      }
    },
  },
})

export const {
  addFieldToProcessedValidationErrors,
  addOrRemoveDraftCommunities,
  addOrRemoveDraftPlanElevation,
  patchDraftPlan,
  patchDraftPlanCommunityValue,
  patchDraftPlanInfo,
  patchDraftPlanInfoForAutoUploadedFile,
  patchDraftPlanModal,
  patchDraftPlanValidationValue,
  patchPlanData,
  patchShellPlanData,
  patchUnitPlanData,
  removeFieldFromProcessedValidationErrors,
  removeSingleFamilyPlanKey,
  renameRegionalPlanNumber,
  resetDraftPlan,
  resetDraftPlanModal,
  resetDraftPlanValidationValue,
  resetElevation,
  resetPlanModalElevations,
  resetTab,
  setAutoExtractedDataProcessed,
  setDocumentPropagationEnabled,
  setDraftPlan,
  setDraftPlanModalValue,
  setElevation,
  setFileVersion,
  setFloorPlanMetadata,
  setIsAutoExtractFromDocument,
  setPlanCategory,
  setPlanData,
  setProcessedValidationErrors,
  setShellPlanData,
  setSpecificationsData,
  setSupplementalDocumentsData,
  setTabValue,
  setUnitPlanData,
} = createPlanSlice.actions

export const createPlanActions = {
  fetchCommunityDataById,
  fetchSourceRegionalPlan,
  getBulkEditPlans,
  getJdePriceStatus,
  getModsySpecs,
  getPlanDataByPlanId,
  getPlanSubset,
  getShellPlan,
  getShellPlanCopy,
  getShellPlanSupplementDocuments,
  getUnitPlan,
  getUnitPlanCopy,
  getUnitPlanSupplementDocuments,
  uploadFileForAutoExtract,
}

export const { reducer: createPlanReducer } = createPlanSlice
